/**
 * ReporteConsumo.jsx
 * ─────────────────────────────────────────────────────────────────────
 * Reporte generativo de cartera de consumo IFRS 9.
 * Mismo patrón visual que ReporteCaptacion / ReporteComercializacion.
 * Secciones: Resumen · Calidad IFRS 9 · Variación de saldos · Por Producto · Territorio
 */

import React, { useState, useCallback, useEffect, useRef, memo } from "react";
import {
  COLOR, fmt, fmtB, fmtPct, fmtN,
  KpiCard, BarRow, SectionHeader, InsightBox,
  MapaEstados, MapaLeyenda, MX_ISO_TO_CVE,
} from "./ReporteComponents";

const API_URL = process.env.REACT_APP_BACKEND_URL || "https://cnbv-agente-196915693363.us-central1.run.app";

const PERIODOS = ["202606","202604","202603","202602","202601","202512","202511","202510","202509"];

const INSTITUCIONES = [
  "CIBanco",
  "Intercam Banco",
  "Sabadell",
  "Banca Mifel",
  "Banco del Bajío",
  "Ve por Más",
  "Banregio",
  "Multiva",
  "Kapital",
  "Bansí",
  "Ualá",
  "Bancrea",
  "Bankaool",
  "Actinver",
  "Banco Plata",
  "Consubanco",
  "Banfeliz",
  "Openbank",
  "Bancoppel",
  "Banco Bineo",
  "Revolut Bank",
  "Banco Azteca",
  "Compartamos",
  "Volkswagen Bank",
  "Hey Banco",
  "Banorte",
  "Scotiabank",
  "Santander",
  "Inbursa",
  "Banamex",
  "BBVA México",
  "HSBC",
  "Invex",
  "Afirme",
];

const SECCIONES = [
  { id: "resumen",    icon: "📋", label: "Resumen"       },
  { id: "calidad",    icon: "◉",  label: "Calidad"       },
  { id: "rollrates",  icon: "⟳",  label: "Var. Saldos"   },
  { id: "producto",   icon: "◆",  label: "Por Producto"  },
  { id: "territorio", icon: "🗺️", label: "Territorio"    },
  { id: "originacion",icon: "⊕",  label: "Originación"   },
  { id: "riesgo",     icon: "🔍", label: "Riesgo"        },
  { id: "pronostico", icon: "📈", label: "Pronóstico"    },
];

const fmtImor = n => n == null ? "—" : `${fmt(n, 2)}%`;

// Etiquetas legibles de productos de consumo (p. ej. "Abcd" → ABCD).
// ABCD = Adquisición de Bienes de Consumo Duradero (es un PRODUCTO, no una clase de riesgo).
function prettyProducto(name) {
  const raw = String(name ?? "").trim();
  const key = raw.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  if (key === "abcd") return "ABCD (Adquisición de Bienes de Consumo Duradero)";
  return raw || "—";
}

// ══════════════════════════════════════════════════════════════════════
// GENERADOR HTML EXPORT
// ══════════════════════════════════════════════════════════════════════
function generarHTMLExport(reporte) {
  const s   = reporte.secciones || {};
  const k   = s.resumen?.kpis || {};
  const per = String(reporte.periodo);
  const perFmt = `${per.slice(0,4)}-${per.slice(4)}`;
  const ent = reporte.nombre_entidad || "Banca Múltiple";

  const fB  = (n, d=2) => `${(n ?? 0).toFixed(d)} mmdp`;
  const fN  = n => (n ?? 0).toLocaleString("es-MX");
  const fP  = n => n == null ? "—" : `${n >= 0 ? "+" : ""}${(n ?? 0).toFixed(1)}%`;
  const fI  = n => n == null ? "—" : `${(n ?? 0).toFixed(2)}%`;
  const esc = v => String(v ?? "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");

  const C = {
    bg:"#070c12", surface:"#0d1520", card:"#111b28", border:"#1a2535",
    text:"#dde4ee", muted:"#8aa4b8", dim:"#556a7a",
    gold:"#c8a96e", goldDim:"#8a6d3b",
    green:"#34d399", red:"#f87171", blue:"#60a5fa", amber:"#fbbf24",
  };

  const kpiCard = (label, value, delta, dl, accent) =>
    `<div style="background:${C.card};border-radius:6px;padding:12px 16px;border:1px solid ${C.border};flex:1;min-width:160px;">
      <div style="font-size:10px;color:${C.muted};font-weight:700;font-family:monospace;text-transform:uppercase;letter-spacing:.6px;margin-bottom:6px;">${esc(label)}</div>
      <div style="font-size:22px;font-weight:800;color:${accent||C.gold};font-family:monospace;">${esc(value)}</div>
      ${delta != null ? `<div style="font-size:11px;margin-top:5px;font-family:monospace;color:${delta>0?C.green:delta<0?C.red:C.muted};">${delta>0?"▲":delta<0?"▼":"─"} ${fP(delta)}${dl?` <span style="color:${C.dim}">${esc(dl)}</span>`:""}</div>` : ""}
    </div>`;

  const insightBox = text => !text ? "" :
    `<div style="background:${C.gold}0d;border:1px solid ${C.goldDim};border-radius:6px;padding:10px 14px;font-size:12px;line-height:1.7;color:${C.text};margin-top:10px;">
      <div style="font-size:9px;font-weight:700;font-family:monospace;background:${C.gold}22;color:${C.gold};display:inline-block;padding:1px 8px;border-radius:4px;margin-bottom:6px;border:1px solid ${C.goldDim};">✦ Comentario analítico</div>
      <div style="white-space:pre-wrap;">${esc(text)}</div>
    </div>`;

  const secHeader = (icon, title) =>
    `<div style="padding:10px 0;margin-bottom:14px;border-bottom:2px solid ${C.gold}44;">
      <span style="font-size:16px;margin-right:8px;">${icon}</span>
      <span style="font-weight:700;font-size:13px;color:${C.gold};font-family:monospace;letter-spacing:0.05em;text-transform:uppercase;">${esc(title)}</span>
    </div>`;

  const barRow = (label, pct, right, color) =>
    `<div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
      <div style="width:160px;font-size:11px;color:${C.muted};font-family:monospace;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex-shrink:0;">${esc(label)}</div>
      <div style="flex:1;height:6px;background:${C.border};border-radius:4px;overflow:hidden;"><div style="width:${Math.min(100,Math.max(1,pct))}%;height:100%;background:${color||C.gold};border-radius:4px;"></div></div>
      <div style="font-size:11px;color:${C.muted};width:100px;font-family:monospace;text-align:right;flex-shrink:0;">${esc(right)}</div>
    </div>`;

  const thS = `padding:8px 12px;text-align:left;color:${C.muted};font-weight:600;border-bottom:1px solid ${C.border};font-size:10px;font-family:monospace;`;
  const tdS = `padding:7px 12px;font-size:10px;font-family:monospace;`;

  // 1. Resumen
  const secResumen = `<section style="margin-bottom:32px;">
    ${secHeader("📋","Resumen ejecutivo")}
    <div style="display:flex;gap:10px;margin-bottom:10px;flex-wrap:wrap;">
      ${kpiCard("Saldo total",       fB(k.saldo_total_b),   k.delta_total_mom, "vs bim.")}
      ${kpiCard("Cartera vigente",   fB(k.saldo_vigente_b))}
      ${kpiCard("Saldo en Etapa 3",  fB(k.saldo_vencido_b), k.delta_venc_mom,  "vs bim.", C.red)}
      ${kpiCard("IMOR",              fI(k.imor),            null, null, k.imor>3?C.red:C.gold)}
      ${kpiCard("ICOR (cobertura)",  fI(k.icor))}
    </div>
    <div style="display:flex;gap:10px;margin-bottom:10px;flex-wrap:wrap;">
      ${kpiCard("Núm. créditos",     fN(k.num_creditos))}
      ${kpiCard("Saldo promedio",    `$${fN(k.saldo_promedio_mxn)} MXN`)}
      ${kpiCard("Tasa ponderada",    fI(k.tasa_ponderada))}
    </div>
    <div style="display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap;">
      ${kpiCard("Crecimiento YoY nominal", fP(k.delta_total_yoy), null, null, k.delta_total_yoy>0?C.green:C.red)}
      ${k.delta_total_yoy_real!=null ? kpiCard("Crecimiento YoY real", fP(k.delta_total_yoy_real), null, null, k.delta_total_yoy_real>0?C.green:C.red) : ""}
    </div>
    ${insightBox(s.resumen?.narrativa)}
  </section>`;

  // 2. Calidad
  const cal = s.calidad || {};
  const etapas = cal.por_etapa || {};
  const secCalidad = `<section style="margin-bottom:32px;">
    ${secHeader("◉","Calidad de Cartera — IFRS 9")}
    <div style="display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap;">
      ${kpiCard("Etapa 1 — Riesgo normal",  fB(etapas.etapa1?.saldo_b), etapas.etapa1?.delta_mom, "vs bim.", C.green)}
      ${kpiCard("Etapa 2 — Riesgo signif.", fB(etapas.etapa2?.saldo_b), etapas.etapa2?.delta_mom, "vs bim.", C.gold)}
      ${kpiCard("Etapa 3 — Deterioro cred.", fB(etapas.etapa3?.saldo_b), etapas.etapa3?.delta_mom, "vs bim.", C.red)}
      ${etapas.valor_razonable ? kpiCard("Valuada a valor razonable", fB(etapas.valor_razonable?.saldo_b), null, null, C.blue) : ""}
    </div>
    <div style="font-size:9px;color:${C.dim};font-family:monospace;margin-bottom:12px;">Conciliación: Etapa 1 + Etapa 2 + Etapa 3 + Valuada a valor razonable = Saldo total${etapas.total_b!=null?` (${fB(etapas.total_b)})`:""}. La Etapa 3 (deterioro crediticio) no equivale a "cartera vencida".</div>
    ${(cal.por_cartera||[]).length > 0 ? `
    <table style="width:100%;border-collapse:collapse;">
      <thead><tr>
        <th style="${thS}">Tipo de cartera</th>
        <th style="${thS}text-align:right;">Saldo</th>
        <th style="${thS}text-align:right;">Saldo E3</th>
        <th style="${thS}text-align:right;">IMOR</th>
        <th style="${thS}text-align:right;">Núm. créditos</th>
      </tr></thead>
      <tbody>${(cal.por_cartera||[]).map(c => `
        <tr style="border-bottom:1px solid ${C.border}33;">
          <td style="${tdS}color:${C.text};">${esc(prettyProducto(c.cartera))}</td>
          <td style="${tdS}text-align:right;color:${C.muted};">${fB(c.saldo_b)}</td>
          <td style="${tdS}text-align:right;color:${C.muted};">${fB(c.vencido_b)}</td>
          <td style="${tdS}text-align:right;color:${c.imor>3?C.red:C.gold};font-weight:700;">${fI(c.imor)}</td>
          <td style="${tdS}text-align:right;color:${C.muted};">${fN(c.num_creditos)}</td>
        </tr>`).join("")}
      </tbody>
    </table>` : ""}
  </section>`;

  // 3. Variación de saldos por etapa
  const rr  = s.migracion_saldos || s.roll_rates || {};
  const mat = rr.matriz_actual || {};
  const rrHist = rr.historico || [];
  const sVar = v => v==null ? "—" : `${v>=0?"+":""}${fmt(v,4)}%`;         // valor con signo
  const cVar = v => v==null ? C.muted : (v>0 ? C.red : C.green);          // aumento E2/E3 = adverso
  const secRollRates = `<section style="margin-bottom:32px;">
    ${secHeader("⟳","Variación Neta de Saldos por Etapa IFRS 9")}
    <div style="display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap;">
      ${kpiCard("Variación Etapa 1", sVar(mat.var_e1_pct), null, null, C.muted)}
      ${kpiCard("Variación Etapa 2", sVar(mat.var_e2_pct), null, null, cVar(mat.var_e2_pct))}
      ${kpiCard("Variación Etapa 3", sVar(mat.var_e3_pct), null, null, cVar(mat.var_e3_pct))}
    </div>
    ${insightBox(rr.narrativa)}
    ${rrHist.length ? `<table style="width:100%;border-collapse:collapse;margin-top:12px;">
      <thead><tr>
        ${["Periodo","Var. E1 %","Var. E2 %","Var. E3 %","E1 (mmdp)","E2 (mmdp)","E3 (mmdp)"].map(h=>`<th style="${thS}text-align:right;">${h}</th>`).join("")}
      </tr></thead>
      <tbody>${[...rrHist].reverse().slice(0,12).map(row=>`<tr style="border-bottom:1px solid ${C.border}22;">
        <td style="${tdS}text-align:right;color:${C.muted};">${String(row.periodo).slice(0,4)}-${String(row.periodo).slice(4)}</td>
        <td style="${tdS}text-align:right;color:${C.muted};">${sVar(row.var_e1_pct)}</td>
        <td style="${tdS}text-align:right;color:${cVar(row.var_e2_pct)};">${sVar(row.var_e2_pct)}</td>
        <td style="${tdS}text-align:right;color:${cVar(row.var_e3_pct)};">${sVar(row.var_e3_pct)}</td>
        <td style="${tdS}text-align:right;color:${C.muted};">${fB(row.e1_b,3)}</td>
        <td style="${tdS}text-align:right;color:${C.muted};">${fB(row.e2_b,3)}</td>
        <td style="${tdS}text-align:right;color:${C.muted};">${fB(row.e3_b,3)}</td>
      </tr>`).join("")}</tbody>
    </table>` : ""}
    <div style="font-size:9px;color:${C.dim};font-family:monospace;margin-top:8px;">* Variación neta del saldo de cada etapa sobre su propio saldo del corte anterior (saldos agregados; no es migración por cohortes). Un aumento en Etapa 2 o 3 es adverso (rojo); una disminución es favorable (verde). Los flujos brutos (originación, curas, transiciones, castigos) no son observables con saldos agregados.</div>
  </section>`;

  // 4. Por Producto
  const prod = s.producto || {};
  const topSaldo = prod.top_saldo || [];
  const topImor  = prod.top_imor  || [];
  const maxSaldoProd = topSaldo[0]?.saldo_b || 1;
  const maxImorProd  = topImor[0]?.imor || 1;
  const secProducto = `<section style="margin-bottom:32px;">
    ${secHeader("◆","Por Producto de Crédito")}
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:16px;">
      <div>
        <div style="font-size:10px;font-weight:700;color:${C.muted};font-family:monospace;text-transform:uppercase;letter-spacing:.5px;margin-bottom:10px;padding-bottom:6px;border-bottom:1px solid ${C.border};">Top por saldo</div>
        ${topSaldo.map((p,i) => barRow(
          prettyProducto(p.tipo_credito || p.cartera),
          (p.saldo_b / maxSaldoProd) * 100,
          `${fB(p.saldo_b,2)} · ${fI(p.imor)}`,
          [C.blue,C.gold,"#a78bfa",C.green,C.amber,C.muted][i]||C.dim
        )).join("")}
      </div>
      <div>
        <div style="font-size:10px;font-weight:700;color:${C.muted};font-family:monospace;text-transform:uppercase;letter-spacing:.5px;margin-bottom:10px;padding-bottom:6px;border-bottom:1px solid ${C.border};">Top por IMOR</div>
        ${topImor.map((p,i) => barRow(
          prettyProducto(p.tipo_credito || p.cartera),
          (p.imor / maxImorProd) * 100,
          fI(p.imor),
          p.imor>5?C.red:p.imor>3?C.amber:C.gold
        )).join("")}
      </div>
    </div>
    ${insightBox(prod.narrativa)}
  </section>`;

  // 5. Territorio — mapa interactivo
  const terr        = Array.isArray(s.territorio) ? s.territorio : [];
  const terrJSON     = JSON.stringify(terr);
  const isoMapJSON   = JSON.stringify(MX_ISO_TO_CVE);
  const munRawExp    = s.territorio_municipio;
  const munDatosExp  = Array.isArray(munRawExp) ? munRawExp : (munRawExp?.datos || []);
  const terrMunJSON  = JSON.stringify(munDatosExp);
  const GEOJSON_MUN_EXP = "https://emrisk-ai-mx.web.app/municipios_mx.geojson";
  const TERR_METRICS = [
    { key: "saldo_b",   label: "Saldo total",   fmt: "b"   },
    { key: "vencido_b", label: "Saldo en Etapa 3", fmt: "b"   },
    { key: "imor",      label: "IMOR",           fmt: "pct" },
  ];
  const terrMetricsJSON = JSON.stringify(TERR_METRICS);

  const secTerritorio = `<section style="margin-bottom:32px;">
    ${secHeader("🗺️","Territorio — Distribución por Estado")}
    <div style="display:flex;gap:6px;flex-wrap:wrap;padding:10px 12px;background:${C.surface};border:1px solid ${C.border};border-radius:8px;margin-bottom:14px;align-items:center;">
      <span style="font-size:10px;color:${C.dim};font-family:monospace;margin-right:4px;">MÉTRICA</span>
      ${TERR_METRICS.map(m => `
      <button class="terr-btn-con" data-metric="${m.key}"
        onclick="switchTerrMetricCon('${m.key}')"
        style="padding:4px 12px;border-radius:4px;font-size:10px;font-family:monospace;cursor:pointer;
               border:1px solid ${m.key==='saldo_b'?C.goldDim:C.border};
               background:${m.key==='saldo_b'?C.gold+'22':'transparent'};
               color:${m.key==='saldo_b'?C.gold:C.muted};
               font-weight:${m.key==='saldo_b'?700:400};">
        ${m.label}
      </button>`).join("")}
    </div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;">
      <div>
        <div style="font-size:10px;font-family:monospace;color:${C.muted};margin-bottom:6px;">
          Mostrando: <span id="terr-metric-label-con" style="color:${C.gold};font-weight:700;">Saldo total</span>
        </div>
        <div id="mapa-estados-con" style="height:380px;border-radius:8px;border:1px solid ${C.border};background:#0a1118;"></div>
        <div style="display:flex;gap:8px;align-items:center;margin-top:8px;font-size:10px;font-family:monospace;color:${C.muted};">
          <span>Bajo</span>
          <div style="width:140px;height:8px;background:linear-gradient(to right,#1e3a8a,#0891b2,#0d9488,#16a34a,#ca8a04,#ea580c,#dc2626);border-radius:4px;"></div>
          <span>Máximo</span>
          <div style="width:10px;height:10px;background:#252836;opacity:0.5;border-radius:2px;margin-left:8px;"></div>
          <span>Sin datos</span>
        </div>
      </div>
      <div style="background:${C.card};border:1px solid ${C.border};border-radius:8px;overflow:hidden;display:flex;flex-direction:column;">
        <div style="padding:8px 14px;background:${C.surface};border-bottom:1px solid ${C.border};font-size:11px;font-family:monospace;color:${C.text};font-weight:600;">
          Ranking — <span id="ranking-metric-label-con">Saldo total</span>
        </div>
        <div id="ranking-list-con" style="flex:1;overflow-y:auto;max-height:380px;"></div>
      </div>
    </div>
    ${munDatosExp.length ? `<div style="margin-top:22px;padding-top:14px;border-top:1px solid ${C.border};">
      <div style="font-size:11px;font-family:monospace;color:${C.muted};margin-bottom:8px;">Mapa Municipal — Saldo total <span style="color:${C.dim};">(${munDatosExp.length} municipios con dato)</span></div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;">
        <div>
          <div id="mapa-mun-con" style="height:380px;border-radius:8px;border:1px solid ${C.border};background:#0a1118;"></div>
          <div style="font-size:9px;color:${C.dim};font-family:monospace;margin-top:6px;">Se muestran los municipios con mayor saldo (subconjunto de hasta 500). Un municipio sin color puede tener cartera de consumo y no estar en este subconjunto; no implica que no reporte.</div>
        </div>
        <div style="background:${C.card};border:1px solid ${C.border};border-radius:8px;overflow:hidden;display:flex;flex-direction:column;">
          <div style="padding:8px 14px;background:${C.surface};border-bottom:1px solid ${C.border};font-size:11px;font-family:monospace;color:${C.text};font-weight:600;">Top municipios — Saldo total</div>
          <div style="flex:1;overflow-y:auto;max-height:380px;">${[...munDatosExp].sort((a,b)=>(b.saldo_b||0)-(a.saldo_b||0)).slice(0,20).map((m,i)=>`<div style="display:flex;align-items:center;gap:8px;padding:5px 14px;border-bottom:1px solid ${C.border}22;">
            <span style="font-size:10px;font-family:monospace;width:20px;text-align:right;color:${i<3?C.gold:C.dim};font-weight:${i<3?700:400};">${i+1}</span>
            <div style="flex:1;overflow:hidden;"><span style="font-size:11px;font-family:monospace;color:${C.text};">${esc(m.municipio||"")}</span> <span style="font-size:10px;font-family:monospace;color:${C.dim};">${esc(m.estado||"")}</span></div>
            <span style="font-size:10px;font-family:monospace;color:${C.gold};">${fB(m.saldo_b,2)}</span>
          </div>`).join("")}</div>
        </div>
      </div>
    </div>` : ""}
  </section>`;

  // 6. Originación (export estático)
  const O   = s.originacion || {};
  const OT  = O.total || {};
  const Ovc = OT.var_corte_anterior || {};
  const Ova = OT.var_anual || {};
  const Oprod = O.productos || [];
  const Oinst = O.instituciones || [];
  const Oconc = O.concentracion;
  const Oseg = O.segmentos || {};
  const Ocal = O.calidad_inicial || {};
  const Ocd  = O.calidad_datos || [];
  const Ocm  = O.contexto_mercado || {};
  const oVar   = v => v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;
  const oPb    = v => v == null ? "—" : `${v >= 0 ? "+" : ""}${v} pb`;
  const oPeso  = v => v == null ? "—" : `$${Math.round(v).toLocaleString("es-MX")}`;
  const oNum   = v => v == null ? "—" : (Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(2)} M` : v.toLocaleString("es-MX"));
  const oTa    = v => v == null ? "—" : `${v.toFixed(2)}%`;
  const oP1    = v => v == null ? "—" : `${v.toFixed(1)}%`;
  const oCol   = v => v == null ? C.muted : v >= 0 ? C.green : C.red;
  const OnoAsal = (Oseg.sector_laboral || []).find(r => /no *asal/i.test(r.clave || ""));
  const Omuj = (Oseg.genero?.agregado || []).find(g => /^m/i.test((g.genero || "").trim()));
  const OgeoTop3 = (Oseg.geografia?.top5 || []).slice(0, 3);
  const OgeoPct = OgeoTop3.reduce((a, r) => a + (r.pct_monto || 0), 0);
  const OpenTop = Oseg.geografia?.penetracion_top || [];
  const OingCero = (Oseg.ingreso?.filas || []).find(r => /cero|no informado/i.test(r.intervalo || ""));
  const thO = `padding:6px 10px;text-align:right;color:${C.muted};font-weight:600;border-bottom:1px solid ${C.border};font-size:10px;font-family:monospace;white-space:nowrap;`;
  const tdOr = `padding:5px 10px;text-align:right;font-size:11px;font-family:monospace;`;
  const tdOl = `padding:5px 10px;text-align:left;font-size:11px;font-family:monospace;color:${C.text};`;
  const segCard = (t, col, body) => `<div style="background:${C.card};border:1px solid ${C.border};border-left:3px solid ${col};border-radius:6px;padding:10px 14px;">
    <div style="font-size:10px;color:${C.muted};font-family:monospace;text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px;">${t}</div>
    <div style="font-size:12px;color:${C.text};line-height:1.55;">${body}</div></div>`;

  const oKpisHtml = [
    ["Monto originado", `${(OT.monto_mdp || 0).toLocaleString("es-MX")} mdp`, Ovc.monto, Ova.monto, false],
    ["Créditos otorgados", oNum(OT.numero_creditos), Ovc.creditos, Ova.creditos, false],
    ["Ticket promedio", oPeso(OT.ticket_promedio), Ovc.ticket, Ova.ticket, false],
    ["Tasa ponderada", oTa(OT.tasa_ponderada), Ovc.tasa_pb, Ova.tasa_pb, true],
  ].map(([l, v, cc, ca, pb]) => `<div style="background:${C.card};border-radius:6px;padding:12px 16px;border:1px solid ${C.border};flex:1;min-width:150px;">
    <div style="font-size:10px;color:${C.muted};font-weight:700;font-family:monospace;text-transform:uppercase;letter-spacing:.6px;">${l}</div>
    <div style="font-size:20px;font-weight:800;color:${C.gold};font-family:monospace;margin:3px 0;">${esc(v)}</div>
    <div style="font-size:10px;color:${C.dim};font-family:monospace;">Corte ant. <span style="color:${oCol(cc)}">${pb ? oPb(cc) : oVar(cc)}</span> · Anual <span style="color:${oCol(ca)}">${pb ? oPb(ca) : oVar(ca)}</span></div></div>`).join("");

  const Ohist = O.historico || [];
  const sparkSVG = (data, acc, color) => {
    const W = 260, H = 44, P = 3;
    const vals = (data || []).map(acc);
    if (vals.length < 2) return "";
    const mn = Math.min(...vals), mx = Math.max(...vals), rg = (mx - mn) || 1;
    const x = i => P + i / (vals.length - 1) * (W - 2 * P);
    const y = v => H - P - (v - mn) / rg * (H - 2 * P);
    const pts = vals.map((v, i) => `${x(i)},${y(v)}`).join(" ");
    return `<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" style="width:100%;height:40px;"><polyline points="${pts}" fill="none" stroke="${color}" stroke-width="1.5"/></svg>`;
  };
  const miniPanel = (label, valor, acc, color) => `<div style="background:${C.card};border:1px solid ${C.border};border-radius:6px;padding:10px 12px;">
    <div style="font-size:10px;color:${C.muted};font-family:monospace;text-transform:uppercase;letter-spacing:.5px;">${label}</div>
    <div style="font-size:16px;font-weight:800;color:${C.gold};font-family:monospace;margin:2px 0 4px;">${esc(valor)}</div>
    ${sparkSVG(Ohist, acc, color)}
    <div style="font-size:9px;color:${C.dim};font-family:monospace;">${Ohist.length} cortes históricos</div></div>`;
  const bubbleSVG = (prods) => {
    const W = 560, H = 340, ML = 58, MB = 42, MT = 40, MR = 20;
    const pts = (prods || []).filter(p => p.tasa != null && p.ticket != null && (p.monto_mdp || 0) > 0);
    if (!pts.length) return "";
    const paleta = [C.blue, C.gold, "#a78bfa", C.green, C.amber, "#ec4899", "#f97316", C.muted];
    const col = p => paleta[pts.indexOf(p) % paleta.length];
    const xMax = Math.max(...pts.map(p => p.tasa)) * 1.1 || 1;
    const yMax = Math.max(...pts.map(p => p.ticket)) * 1.18 || 1;
    const mMax = Math.max(...pts.map(p => p.monto_mdp)) || 1;
    const px = v => ML + v / xMax * (W - ML - MR);
    const py = v => H - MB - v / yMax * (H - MB - MT);
    const pr = v => 7 + Math.sqrt(v / mMax) * 26;
    const xt = [0, .25, .5, .75, 1].map(t => t * xMax), yt = [0, .5, 1].map(t => t * yMax);
    const draw = [...pts].sort((a, b) => b.monto_mdp - a.monto_mdp);
    const labs = pts.map(p => ({ p, cx: px(p.tasa), cy: py(p.ticket), r: pr(p.monto_mdp) })).sort((a, b) => a.cx - b.cx);
    let lx = -999, ly = -999;
    labs.forEach(l => { let y = l.cy - l.r - 7; if (Math.abs(l.cx - lx) < 78 && Math.abs(y - ly) < 13) y = ly - 13; l.ly = y; lx = l.cx; ly = y; });
    const sh = t => (t || "").length > 22 ? t.slice(0, 21) + "…" : t;
    return `<svg viewBox="0 0 ${W} ${H}" style="width:100%;max-width:600px;height:auto;">
      ${yt.map(v => `<line x1="${ML}" y1="${py(v)}" x2="${W - MR}" y2="${py(v)}" stroke="${C.border}" stroke-width="0.5"/><text x="${ML - 6}" y="${py(v) + 3}" text-anchor="end" font-size="9" fill="${C.dim}" font-family="monospace">$${Math.round(v / 1000)}K</text>`).join("")}
      ${xt.map(v => `<text x="${px(v)}" y="${H - MB + 14}" text-anchor="middle" font-size="9" fill="${C.dim}" font-family="monospace">${v.toFixed(0)}%</text>`).join("")}
      <text x="${(ML + W - MR) / 2}" y="${H - 5}" text-anchor="middle" font-size="10" fill="${C.muted}" font-family="monospace">Tasa ponderada</text>
      <text x="13" y="${(MT + H - MB) / 2}" text-anchor="middle" font-size="10" fill="${C.muted}" font-family="monospace" transform="rotate(-90 13 ${(MT + H - MB) / 2})">Ticket promedio</text>
      ${draw.map(p => `<circle cx="${px(p.tasa)}" cy="${py(p.ticket)}" r="${pr(p.monto_mdp)}" fill="${col(p)}" fill-opacity="0.3" stroke="${col(p)}" stroke-width="1.3"/>`).join("")}
      ${labs.map(l => `<line x1="${l.cx}" y1="${l.cy - l.r}" x2="${l.cx}" y2="${l.ly + 2}" stroke="${col(l.p)}" stroke-width="0.6" opacity="0.6"/><text x="${l.cx}" y="${l.ly}" text-anchor="middle" font-size="9.5" fill="${C.text}" font-family="monospace">${esc(sh(l.p.producto))}</text>`).join("")}
    </svg>`;
  };

  const secOriginacion = O && (OT.monto_mdp || Oprod.length) ? `<section style="margin-bottom:32px;">
    ${secHeader("⊕", "Originación de cartera de consumo")}
    <div style="font-size:11px;color:${C.dim};font-family:monospace;margin:-8px 0 12px;">${esc(O.alcance === "institucional" ? "Institución" : "Banca múltiple")} · ${esc(perFmt)} · créditos marginales · cortes ${esc(O.periodicidad || "—")}</div>
    ${insightBox(O.narrativa)}
    <div style="display:flex;gap:10px;flex-wrap:wrap;margin:14px 0 18px;">${oKpisHtml}</div>

    ${Ohist.length > 1 ? `<div style="font-size:12px;font-weight:700;color:${C.text};margin-bottom:8px;">¿La originación crece por volumen, ticket o precio?</div>
    <div style="display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:10px;margin-bottom:18px;">
      ${miniPanel("Monto originado", `${(OT.monto_mdp || 0).toLocaleString("es-MX")} mdp`, d => d.monto_mdp, C.gold)}
      ${miniPanel("Número de créditos", oNum(OT.numero_creditos), d => d.creditos, C.blue)}
      ${miniPanel("Ticket promedio", oPeso(OT.ticket_promedio), d => d.ticket, C.green)}
      ${miniPanel("Tasa ponderada", oTa(OT.tasa_ponderada), d => d.tasa, "#a78bfa")}
    </div>` : ""}

    <div style="font-size:12px;font-weight:700;color:${C.text};margin-bottom:8px;">¿Qué productos explican la originación?</div>
    <table style="width:100%;border-collapse:collapse;margin-bottom:6px;">
      <thead><tr>
        <th style="${thO}text-align:left;">Producto</th><th style="${thO}">Monto mdp</th><th style="${thO}">% monto</th>
        <th style="${thO}">Créditos</th><th style="${thO}">Ticket</th><th style="${thO}">Tasa</th>
        <th style="${thO}">Var. anual</th><th style="${thO}">Δ tasa</th>
      </tr></thead>
      <tbody>${Oprod.map(p => `<tr style="border-bottom:1px solid ${C.border}33;">
        <td style="${tdOl}">${esc(p.producto)}</td>
        <td style="${tdOr}color:${C.text};">${(p.monto_mdp || 0).toLocaleString("es-MX")}</td>
        <td style="${tdOr}color:${C.muted};">${oP1(p.pct_monto)}</td>
        <td style="${tdOr}color:${C.text};">${oNum(p.creditos)}</td>
        <td style="${tdOr}color:${C.text};">${oPeso(p.ticket)}</td>
        <td style="${tdOr}color:${C.gold};">${oTa(p.tasa)}</td>
        <td style="${tdOr}color:${oCol(p.var_anual_monto)};">${oVar(p.var_anual_monto)}</td>
        <td style="${tdOr}color:${p.var_anual_tasa_pb == null ? C.muted : p.var_anual_tasa_pb >= 0 ? C.red : C.green};">${oPb(p.var_anual_tasa_pb)}</td>
      </tr>`).join("")}</tbody>
    </table>
    ${O.descomposicion_tasa?.delta_total_pb != null ? `<div style="font-size:11px;color:${C.muted};margin-bottom:10px;line-height:1.5;"><b style="color:${C.gold};">Descomposición de la tasa:</b> total ${oPb(O.descomposicion_tasa.delta_total_pb)} = efecto precio ${oPb(O.descomposicion_tasa.efecto_precio_pb)} + efecto mezcla ${oPb(O.descomposicion_tasa.efecto_mezcla_pb)}.</div>` : ""}
    ${(Ocm.tasa_referencia != null || Ocm.icc) ? `<div style="font-size:11px;color:${C.muted};margin-bottom:16px;line-height:1.5;"><b style="color:${C.gold};">Contexto de mercado:</b> ${Ocm.tasa_referencia != null ? `tasa objetivo Banxico ${Ocm.tasa_referencia.toFixed(2)}%` : ""}${Ocm.spread_pb != null ? ` · spread originación–tasa obj. ${Ocm.spread_pb >= 0 ? "+" : ""}${Ocm.spread_pb} pb` : ""}${Ocm.icc?.valor != null ? ` · ICC ${Ocm.icc.valor}${Ocm.icc.var_anual != null ? ` (${Ocm.icc.var_anual >= 0 ? "+" : ""}${Ocm.icc.var_anual} pts anual)` : ""}` : ""}.</div>` : ""}

    ${bubbleSVG(Oprod) ? `<div style="font-size:12px;font-weight:700;color:${C.text};margin:8px 0;">Matriz tasa · ticket · monto por producto</div>
    <div style="margin-bottom:16px;">${bubbleSVG(Oprod)}<div style="font-size:9px;color:${C.dim};font-family:monospace;">Tamaño de burbuja ∝ monto originado.</div></div>` : ""}

    ${Oinst.length ? `<div style="font-size:12px;font-weight:700;color:${C.text};margin:8px 0;">¿Quién está colocando el crédito?</div>
    <table style="width:100%;border-collapse:collapse;margin-bottom:6px;">
      <thead><tr>
        <th style="${thO}text-align:left;">Institución</th><th style="${thO}">Monto mdp</th><th style="${thO}">% monto</th>
        <th style="${thO}">% créd.</th><th style="${thO}">Ticket</th><th style="${thO}">Tasa</th>
      </tr></thead>
      <tbody>${Oinst.map(e => `<tr style="border-bottom:1px solid ${C.border}33;${e.institucion === "Otros" ? `background:${C.surface}80;` : ""}">
        <td style="${tdOl}">${esc(e.institucion)}</td>
        <td style="${tdOr}color:${C.text};">${(e.monto_mdp || 0).toLocaleString("es-MX")}</td>
        <td style="${tdOr}color:${C.muted};">${oP1(e.pct_monto)}</td>
        <td style="${tdOr}color:${C.muted};">${oP1(e.pct_creditos)}</td>
        <td style="${tdOr}color:${C.text};">${e.ticket != null ? oPeso(e.ticket) : "—"}</td>
        <td style="${tdOr}color:${C.gold};">${e.tasa != null ? oTa(e.tasa) : "—"}</td>
      </tr>`).join("")}</tbody>
    </table>
    ${Oconc ? `<div style="font-size:11px;color:${C.muted};margin-bottom:10px;">Concentración por monto: HHI ${Oconc.hhi_monto}; CR4 ${Oconc.cr4_monto}%. Por número de créditos: HHI ${Oconc.hhi_creditos}.</div>
    <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:16px;">${(Oconc.hhi_por_producto || []).slice(0, 3).map(h => `<div style="background:${C.card};border:1px solid ${C.border};border-radius:6px;padding:8px 12px;flex:1;min-width:120px;text-align:center;">
      <div style="font-size:10px;color:${C.muted};font-family:monospace;">${esc(h.producto)}</div>
      <div style="font-size:15px;font-weight:800;color:${C.gold};font-family:monospace;">HHI ${(h.hhi || 0).toLocaleString("es-MX")}</div>
      <div style="font-size:9px;color:${C.dim};font-family:monospace;">${esc(h.lider || "")} ${oP1(h.part_lider)}</div></div>`).join("")}</div>` : ""}` : ""}

    <div style="font-size:12px;font-weight:700;color:${C.text};margin:8px 0;">¿A quién y dónde?</div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:8px;">
      ${OnoAsal ? segCard("Sector laboral", C.blue, `No asalariados: ${oP1(OnoAsal.pct_creditos)} de los créditos, pero ${oP1(OnoAsal.pct_monto)} del monto. Ticket ${oPeso(OnoAsal.ticket)} y tasa ${oTa(OnoAsal.tasa)}.`) : ""}
      ${Omuj ? segCard("Género", C.blue, `Mujeres: ${oP1(Omuj.pct_creditos)} de créditos y ${oP1(Omuj.pct_monto)} del monto. La brecha de tasa depende de la mezcla de productos.`) : ""}
      ${OgeoTop3.length ? segCard("Geografía", C.blue, `${OgeoTop3.map(r => esc(r.estado)).join(", ")} concentran ${oP1(OgeoPct)} del monto. HHI estatal ${Oseg.geografia?.hhi_territorial ?? "—"}.${OpenTop[0] ? ` Mayor penetración: ${esc(OpenTop[0].estado)} (${OpenTop[0].pen_monto} mdp/1,000 hab).` : ""}`) : ""}
      ${OpenTop.length ? segCard("Penetración por 1,000 hab (Censo 2020)", C.blue, `<table style="width:100%;border-collapse:collapse;">
        <thead><tr><th style="${thO}text-align:left;">Estado</th><th style="${thO}">mdp/1k</th><th style="${thO}">créd./1k</th></tr></thead>
        <tbody>${OpenTop.map(r => `<tr style="border-bottom:1px solid ${C.border}22;"><td style="${tdOl}">${esc(r.estado)}</td><td style="${tdOr}color:${C.gold};">${r.pen_monto}</td><td style="${tdOr}color:${C.muted};">${r.pen_creditos}</td></tr>`).join("")}</tbody></table>`) : ""}
      ${Ocal.part_e2e3 != null ? segCard("Calidad inicial", C.red, `${oTa(Ocal.part_e2e3)} del monto en etapas 2 o 3${Ocal.cambio_corte_anterior_pb != null ? ` (${oPb(Ocal.cambio_corte_anterior_pb)} vs. corte ant.)` : ""}. No se asume deterioro.${Ocal.valor_razonable_mdp ? ` Valor razonable: ${Ocal.valor_razonable_mdp.toLocaleString("es-MX")} mdp.` : ""}`) : ""}
      ${OingCero ? segCard("Cobertura del dato", C.amber, `Ingreso “0”: ${oP1(OingCero.pct_creditos)} de créditos y ${oP1(OingCero.pct_monto)} del monto (cobertura ${oP1(Oseg.ingreso?.cobertura_creditos)}). Validar significado.`) : ""}
    </div>

    ${Ocd.length ? `<div style="background:${C.red}12;border:1px solid ${C.red}44;border-radius:6px;padding:10px 14px;margin-top:8px;">
      <div style="font-size:11px;font-weight:700;color:${C.red};font-family:monospace;margin-bottom:6px;">Validaciones / calidad de datos</div>
      ${Ocd.map(c => `<div style="font-size:11px;color:${C.muted};font-family:monospace;line-height:1.7;">• ${esc(c.detalle)}</div>`).join("")}</div>` : ""}
  </section>` : "";

  // 7. Riesgo (HHI + anomalías) — export estático
  const Rconc = s.concentracion || {};
  const Ranom = s.anomalias || {};
  const rCatCol = c => c === "Alta" ? C.red : c === "Moderada" ? C.gold : C.green;
  const secRiesgo = (Rconc.filas?.length || Ranom.filas?.length) ? `<section style="margin-bottom:32px;">
    ${secHeader("🔍", "Riesgo — Concentración y anomalías")}
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;">
      <div>
        <div style="font-size:11px;color:${C.muted};font-family:monospace;text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px;">Concentración HHI</div>
        ${Rconc.insight ? `<div style="font-size:12px;color:${C.muted};line-height:1.55;margin-bottom:8px;">${esc(Rconc.insight)}</div>` : ""}
        ${(Rconc.filas || []).map(f => `<div style="display:flex;justify-content:space-between;padding:5px 0;border-bottom:1px solid ${C.border}33;font-size:12px;font-family:monospace;">
          <span style="color:${C.text};overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:55%;">${esc(f.descripcion_estado || "")}${f.banco_dominante ? `<span style="color:${C.dim};"> · ${esc(f.banco_dominante)} ${f.cuota_dominante_pct != null ? f.cuota_dominante_pct.toFixed(0) + "%" : ""}</span>` : ""}</span>
          <span style="color:${rCatCol(f.categoria_hhi)};font-weight:700;">HHI ${Math.round(f.hhi_actual || 0)}${f.delta_hhi ? (f.delta_hhi > 0 ? " ▲" : " ▼") : ""}</span>
        </div>`).join("") || `<div style="font-size:12px;color:${C.dim};font-family:monospace;">Sin datos de concentración</div>`}
      </div>
      <div>
        <div style="font-size:11px;color:${C.muted};font-family:monospace;text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px;">Anomalías estadísticas</div>
        ${(Ranom.filas || []).slice(0, 8).map(a => `<div style="padding:6px 0;border-bottom:1px solid ${C.border}33;font-family:monospace;">
          <div style="font-size:12px;color:${C.text};">${esc(a.nombre_entidad || "")}</div>
          <div style="font-size:11px;color:${C.dim};">${esc(a.actividad_economica || "")}</div>
          <div style="font-size:11px;color:${C.muted};">IMOR ${fI(a.imor)} · Δ ${a.delta_imor_pp != null ? `${a.delta_imor_pp > 0 ? "+" : ""}${a.delta_imor_pp.toFixed(2)} pp` : "—"} · z=${a.z_score != null ? a.z_score.toFixed(1) : "—"} · <span style="color:${(a.tipo_anomalia || "").includes("verificar") ? C.amber : a.z_score > 2.5 ? C.red : a.z_score > 2 ? C.amber : a.z_score < -2 ? C.green : C.muted};font-weight:700;">${esc(a.tipo_anomalia || "")}</span></div>
        </div>`).join("") || `<div style="font-size:12px;color:${C.dim};font-family:monospace;">Sin anomalías de IMOR relevantes en el periodo.</div>`}
      </div>
    </div>
  </section>` : "";

  // 8. Pronóstico (ARIMA) — export estático
  const Fc = s.forecast || {};
  const fcTabla = (proys, unidad, dec) => `<table style="width:100%;border-collapse:collapse;font-size:12px;font-family:monospace;margin-bottom:8px;">
    <thead><tr>${["Periodo", "Proyección", "IC Bajo 90%", "IC Alto 90%"].map(h => `<th style="${thO}">${h}</th>`).join("")}</tr></thead>
    <tbody>${(proys || []).map(p => `<tr style="border-bottom:1px solid ${C.border}33;">
      <td style="${tdOr}color:${C.gold};">${String(p.periodo).slice(0, 4)}-${String(p.periodo).slice(4)}</td>
      <td style="${tdOr}color:${C.text};font-weight:600;">${unidad === "mmdp" ? (p.valor / 1e9).toFixed(dec) + " mmdp" : p.valor.toFixed(dec) + "%"}</td>
      <td style="${tdOr}color:${C.muted};">${p.lower != null ? (unidad === "mmdp" ? (p.lower / 1e9).toFixed(dec) : p.lower.toFixed(dec) + "%") : "—"}</td>
      <td style="${tdOr}color:${C.muted};">${p.upper != null ? (unidad === "mmdp" ? (p.upper / 1e9).toFixed(dec) : p.upper.toFixed(dec) + "%") : "—"}</td>
    </tr>`).join("")}</tbody></table>`;
  const secPronostico = Fc.cubierto ? `<section style="margin-bottom:32px;">
    ${secHeader("📈", "Pronóstico (ARIMA)")}
    <div style="font-size:11px;color:${C.muted};font-family:monospace;line-height:1.5;background:${C.surface};border:1px solid ${C.border}55;border-radius:6px;padding:8px 12px;margin-bottom:14px;">
      <b style="color:${C.gold};">Universo del pronóstico:</b> subconjunto de ${Fc.entidades_modelo?.length || 0} instituciones con historia ARIMA completa${Fc.cobertura_pct != null ? ` (${Fc.cobertura_pct}% del saldo del sistema)` : ""}. Las cifras observadas y proyectadas corresponden a ese subconjunto y <b>no</b> son comparables con el saldo total del reporte. El horizonte inicia en el corte posterior al último observado.
    </div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;">
      <div>
        <div style="font-size:11px;color:${C.muted};font-family:monospace;text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px;">Saldo total — proyección</div>
        ${fcTabla(Fc.saldo?.proyecciones, "mmdp", 2)}
        ${insightBox(Fc.saldo?.insight)}
      </div>
      <div>
        <div style="font-size:11px;color:${C.muted};font-family:monospace;text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px;">IMOR — proyección</div>
        ${fcTabla(Fc.imor?.proyecciones, "%", 4)}
        ${insightBox(Fc.imor?.insight)}
      </div>
    </div>
    <div style="font-size:10px;color:${C.dim};font-family:monospace;margin-top:8px;">Modelos ARIMA_PLUS (BigQuery ML) · ${Fc.entidades_modelo?.length || 0} instituciones${Fc.cobertura_pct != null ? ` · Cobertura ${Fc.cobertura_pct}% del sistema` : ""} · Horizonte ${Fc.horizonte || "—"} cortes bimestrales · IC 90%.</div>
  </section>` : (Fc.advertencia ? `<section style="margin-bottom:32px;">${secHeader("📈", "Pronóstico (ARIMA)")}<div style="font-size:12px;color:${C.muted};font-family:monospace;">${esc(Fc.advertencia)}</div></section>` : "");

  // Script mapa
  const mapScript = `
var _terrDataCon=${terrJSON};
var _isoMapCon=${isoMapJSON};
var _metricsCon=${terrMetricsJSON};
var _currentMetricCon='saldo_b';
var _geoLayerCon=null;
var _terrIdxCon={};
var _aliasCon={
  "michoacan de ocampo":"michoacan","coahuila de zaragoza":"coahuila",
  "veracruz de ignacio de la llave":"veracruz","ciudad de mexico":"distrito federal",
  "michoacan":"michoacan de ocampo","coahuila":"coahuila de zaragoza",
  "veracruz":"veracruz de ignacio de la llave","distrito federal":"ciudad de mexico"
};
function _normCon(s){return(s||"").toLowerCase().normalize("NFD").replace(/[\\u0300-\\u036f]/g,"").replace(/[^a-z ]/g,"").trim().replace(/\\s+/g," ");}
_terrDataCon.forEach(function(d){var k=_normCon(d.estado||"");if(k){_terrIdxCon[k]=d;var a=_aliasCon[k];if(a)_terrIdxCon[a]=d;}});
function _lookupFeatCon(f){var r=_normCon(f.properties.ESTADO||f.properties.estado||f.properties.name||f.properties.NAME||"");return _terrIdxCon[r]||_terrIdxCon[_aliasCon[r]]||null;}
function _sortedValsCon(m){return _terrDataCon.map(function(d){return d[m]||0;}).filter(function(v){return v>0;}).sort(function(a,b){return a-b;});}
function _csCon(v,sv){if(!v||v<=0||!sv.length)return'#1a2535';var lo=0,hi=sv.length-1;while(lo<hi){var m=(lo+hi)>>1;if(sv[m]<v)lo=m+1;else hi=m;}var p=sv.length>1?lo/(sv.length-1):1;return p<0.08?'#1e3a8a':p<0.20?'#0369a1':p<0.34?'#0891b2':p<0.50?'#0d9488':p<0.65?'#16a34a':p<0.80?'#ca8a04':p<0.92?'#ea580c':'#dc2626';}
function _rankColorCon(i,total){var p=total>1?(total-1-i)/(total-1):1;return p<0.08?'#1e3a8a':p<0.20?'#0369a1':p<0.34?'#0891b2':p<0.50?'#0d9488':p<0.65?'#16a34a':p<0.80?'#ca8a04':p<0.92?'#ea580c':'#dc2626';}
function _fmtValCon(val,fmt){if(fmt==='b')return(val||0).toFixed(2)+' mmdp';if(fmt==='pct')return(val||0).toFixed(2)+'%';return(val||0).toLocaleString('es-MX');}
function _tipHtmlCon(d,name){var nd=d||{};return'<b style="font-family:monospace">'+(nd.estado||name)+'</b><br>'+'<span style="color:#c8a96e">Saldo: '+_fmtValCon(nd.saldo_b,'b')+'</span><br>'+'Saldo E3: '+_fmtValCon(nd.vencido_b,'b')+'<br>'+'IMOR: '+_fmtValCon(nd.imor,'pct')+'<br>'+'Núm. créditos: '+(nd.num_creditos||0).toLocaleString('es-MX');}
function _buildRankingCon(metric){var mDef=_metricsCon.find(function(m){return m.key===metric;})||_metricsCon[0];var sorted=[..._terrDataCon].sort(function(a,b){return(b[metric]||0)-(a[metric]||0);});var maxV=sorted.length&&sorted[0][metric]?sorted[0][metric]:1;var total=sorted.length;return sorted.map(function(e,i){var val=e[metric]||0,pct=maxV>0?(val/maxV)*100:0;var color;if(metric==='imor')color=val>3?'#f87171':val>2?'#fbbf24':'#34d399';else color=_rankColorCon(i,total);return'<div style="display:flex;align-items:center;gap:8px;padding:5px 14px;border-bottom:1px solid #1a253522;background:'+(i<3?color+'10':'transparent')+';"><span style="font-size:10px;font-family:monospace;width:20px;text-align:right;color:'+(i<3?color:'#556a7a')+';font-weight:'+(i<3?700:400)+';"> '+(i+1)+'</span><div style="width:160px;flex-shrink:0;overflow:hidden;"><div style="font-size:11px;font-family:monospace;color:'+(i<3?'#dde4ee':'#8aa4b8')+';white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">'+(e.estado||'')+'</div></div><div style="flex:1;height:5px;background:#1a2535;border-radius:3px;overflow:hidden;"><div style="width:'+Math.max(1,pct)+'%;height:100%;background:'+color+';border-radius:3px;"></div></div><span style="font-size:10px;font-family:monospace;width:90px;text-align:right;color:'+color+';">'+_fmtValCon(val,mDef.fmt)+'</span></div>';}).join('');}
function switchTerrMetricCon(metric){_currentMetricCon=metric;var mDef=_metricsCon.find(function(m){return m.key===metric;})||_metricsCon[0];document.querySelectorAll('.terr-btn-con').forEach(function(btn){var active=btn.dataset.metric===metric;btn.style.background=active?'#c8a96e22':'transparent';btn.style.color=active?'#c8a96e':'#8aa4b8';btn.style.border='1px solid '+(active?'#8a6d3b':'#1a2535');btn.style.fontWeight=active?700:400;});var lbl=document.getElementById('terr-metric-label-con');if(lbl)lbl.textContent=mDef.label;var rlbl=document.getElementById('ranking-metric-label-con');if(rlbl)rlbl.textContent=mDef.label;var rl=document.getElementById('ranking-list-con');if(rl)rl.innerHTML=_buildRankingCon(metric);if(_geoLayerCon){var sv=_sortedValsCon(metric);_geoLayerCon.setStyle(function(f){var d=_lookupFeatCon(f);if(!d||!(d[metric]>0))return{fillColor:'#252836',fillOpacity:0.35,color:'#2a2f40',weight:0.5};return{fillColor:_csCon(d[metric],sv),fillOpacity:0.85,color:'#070c12',weight:0.8};});_geoLayerCon.eachLayer(function(layer){var f=layer.feature,d=_lookupFeatCon(f);if(d&&layer.getTooltip)layer.setTooltipContent(_tipHtmlCon(d,f.properties.name||''));});}}
(function(){var rl=document.getElementById('ranking-list-con');if(rl)rl.innerHTML=_buildRankingCon('saldo_b');var map=L.map('mapa-estados-con',{zoomControl:true,attributionControl:false}).setView([23.5,-102],5);L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png',{maxZoom:10}).addTo(map);fetch('https://raw.githubusercontent.com/angelnmara/geojson/master/mexicoHigh.json').then(function(r){return r.json();}).then(function(geo){var sv=_sortedValsCon('saldo_b');_geoLayerCon=L.geoJSON(geo,{style:function(f){var d=_lookupFeatCon(f);if(!d||!d.saldo_b)return{fillColor:'#252836',fillOpacity:0.35,color:'#2a2f40',weight:0.5};return{fillColor:_csCon(d.saldo_b,sv),fillOpacity:0.85,color:'#070c12',weight:0.8};},onEachFeature:function(f,layer){var d=_lookupFeatCon(f);layer.bindTooltip(d?_tipHtmlCon(d,f.properties.name||''):(f.properties.name||''),{sticky:true});layer.on('mouseover',function(){layer.setStyle({weight:2,color:'#c8a96e',fillOpacity:1});});layer.on('mouseout',function(){if(_geoLayerCon)_geoLayerCon.resetStyle(layer);});}}).addTo(map);}).catch(function(){});})();
(function(){var md=${terrMunJSON};if(!md.length)return;var idx={};md.forEach(function(d){if(d.cvegeo)idx[String(d.cvegeo)]=d;});var sv=md.map(function(d){return d.saldo_b||0;}).filter(function(v){return v>0;}).sort(function(a,b){return a-b;});if(!document.getElementById('mapa-mun-con'))return;var mapm=L.map('mapa-mun-con',{zoomControl:true,attributionControl:false}).setView([23.5,-102],5);L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png',{maxZoom:11}).addTo(mapm);function _cvm(f){var p=f.properties||{};return String(p.CVEGEO||p.cvegeo||p.CVE_GEO||'');}fetch('${GEOJSON_MUN_EXP}').then(function(r){return r.json();}).then(function(geo){L.geoJSON(geo,{style:function(f){var d=idx[_cvm(f)];if(!d||!(d.saldo_b>0))return{fillColor:'#252836',fillOpacity:0.25,color:'#2a2f40',weight:0.3};return{fillColor:_csCon(d.saldo_b,sv),fillOpacity:0.85,color:'#070c12',weight:0.4};},onEachFeature:function(f,layer){var d=idx[_cvm(f)];if(d)layer.bindTooltip('<b style="font-family:monospace">'+(d.municipio||'')+', '+(d.estado||'')+'</b><br>Saldo: '+(d.saldo_b||0).toFixed(2)+' mmdp<br>IMOR: '+(d.imor||0).toFixed(2)+'%<br>Núm. créditos: '+(d.num_creditos||0).toLocaleString('es-MX'),{sticky:true});}}).addTo(mapm);}).catch(function(){});})();`;

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Cartera de Consumo ${ent} — ${perFmt}</title>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{background:${C.bg};color:${C.text};font-family:'DM Sans',system-ui,sans-serif;padding:32px 40px;max-width:1100px;margin:auto;}
  h1{font-size:20px;font-weight:800;color:${C.gold};font-family:monospace;letter-spacing:.05em;}
  @media print{body{padding:16px}section{page-break-inside:avoid}#mapa-estados-con{height:280px!important;}}
  .leaflet-tooltip{background:#0d1520;border:1px solid #1a2535;color:#dde4ee;font-family:monospace;font-size:11px;}
</style>
</head>
<body>
  <div style="margin-bottom:28px;padding-bottom:16px;border-bottom:2px solid ${C.gold}44;">
    <h1>◈ CARTERA DE CONSUMO IFRS 9</h1>
    <div style="font-size:13px;color:${C.muted};font-family:monospace;margin-top:6px;">
      ${esc(ent)} · Periodo ${esc(perFmt)} · Generado ${new Date().toLocaleString("es-MX")}
    </div>
  </div>
  ${secResumen}
  ${secCalidad}
  ${secRollRates}
  ${secProducto}
  ${secOriginacion}
  ${secTerritorio}
  ${secRiesgo}
  ${secPronostico}
  <div style="margin-top:40px;padding-top:16px;border-top:1px solid ${C.border};font-size:9px;color:${C.dim};font-family:monospace;">
    Reporte generado automáticamente por EMRISK · CNBV · Datos: Banca Múltiple · Vista: cnbv_con_saldo_vw
  </div>
  <script>${mapScript}<\/script>
</body>
</html>`;
}

// ══════════════════════════════════════════════════════════════════════
// SECCIÓN: RESUMEN
// ══════════════════════════════════════════════════════════════════════
function SeccionResumen({ s }) {
  const k = s?.resumen?.kpis || {};
  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <KpiCard label="Saldo Total"     value={fmtB(k.saldo_total_b)}   unit="" delta={k.delta_total_mom}  deltaLabel="vs bim." />
        <KpiCard label="Cartera Vigente" value={fmtB(k.saldo_vigente_b)} unit="" />
        <KpiCard label="Saldo en Etapa 3" value={fmtB(k.saldo_vencido_b)} unit="" delta={k.delta_venc_mom}   deltaLabel="vs bim." accentColor={COLOR.red} />
        <KpiCard label="IMOR"            value={fmtImor(k.imor)}         unit="" accentColor={k.imor > 3 ? COLOR.red : k.imor > 2 ? COLOR.gold : COLOR.green} />
        <KpiCard label="ICOR (cobertura)" value={fmtImor(k.icor)}        unit="" accentColor={COLOR.muted} />
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <KpiCard label="Núm. Créditos"    value={fmtN(k.num_creditos)}              unit="" />
        <KpiCard label="Saldo Promedio"   value={`$${fmtN(k.saldo_promedio_mxn)}`}  unit="MXN" />
        <KpiCard label="Tasa Ponderada"   value={k.tasa_ponderada != null ? `${fmt(k.tasa_ponderada,2)}%` : "—"} unit="" />
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <KpiCard label="YoY Nominal"  value={fmtPct(k.delta_total_yoy)}       unit="" accentColor={k.delta_total_yoy > 0 ? COLOR.green : COLOR.red} />
        {k.delta_total_yoy_real != null && (
          <KpiCard label="YoY Real" value={fmtPct(k.delta_total_yoy_real)} unit="" accentColor={k.delta_total_yoy_real > 0 ? COLOR.green : COLOR.red} />
        )}
        {k.tipo_cambio_fix && <KpiCard label="FIX Banxico" value={`$${fmt(k.tipo_cambio_fix, 2)}`} unit="MXN/USD" accentColor={COLOR.muted} />}
      </div>
      <InsightBox text={s?.resumen?.narrativa} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SECCIÓN: CALIDAD IFRS 9
// ══════════════════════════════════════════════════════════════════════
function SeccionCalidad({ s }) {
  const cal = s?.calidad || {};
  const { por_etapa, por_cartera } = cal;
  const etapas = [
    { key: "etapa1", label: "Etapa 1 — Riesgo normal",           color: COLOR.green },
    { key: "etapa2", label: "Etapa 2 — Riesgo significativo",    color: COLOR.gold  },
    { key: "etapa3", label: "Etapa 3 — Deterioro crediticio",    color: COLOR.red   },
  ];
  if (por_etapa?.valor_razonable) {
    etapas.push({ key: "valor_razonable", label: "Valuada a valor razonable", color: COLOR.blue });
  }
  const maxSaldo = Math.max(...etapas.map(e => por_etapa?.[e.key]?.saldo_b || 0), 0.001);
  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        {etapas.map(e => {
          const d = por_etapa?.[e.key] || {};
          return <KpiCard key={e.key} label={e.label} value={fmtB(d.saldo_b)} unit=""
            delta={d.delta_mom} deltaLabel="vs bim." accentColor={e.color} />;
        })}
      </div>
      {etapas.map(e => {
        const d = por_etapa?.[e.key] || {};
        return (
          <BarRow key={e.key} label={e.label} value={d.saldo_b || 0} maxValue={maxSaldo}
            color={e.color} rightLabel={`${fmt(d.pct, 1)}% del total`} />
        );
      })}
      {por_cartera?.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace",
                        textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8 }}>
            Por tipo de cartera
          </div>
          {por_cartera.map((c, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between",
                                  padding: "6px 0", borderBottom: `1px solid ${COLOR.border}33`,
                                  fontSize: 14, fontFamily: "monospace" }}>
              <span style={{ color: COLOR.text }}>{prettyProducto(c.cartera)}</span>
              <span style={{ color: COLOR.muted, marginLeft: 12 }}>
                {fmtB(c.saldo_b)} · <span style={{ color: c.imor > 3 ? COLOR.red : COLOR.gold }}>
                  {fmtImor(c.imor)}
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 12, fontSize: 12, color: COLOR.dim, fontFamily: "monospace" }}>
        Conciliación: Etapa 1 + Etapa 2 + Etapa 3 + Valuada a valor razonable = Saldo total
        {por_etapa?.total_b != null ? ` (${fmtB(por_etapa.total_b)})` : ""}. La Etapa 3 (deterioro crediticio) no equivale a “cartera vencida”.
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SECCIÓN: ROLL RATES
// ══════════════════════════════════════════════════════════════════════
function SeccionRollRates({ s }) {
  const rr  = s?.migracion_saldos || s?.roll_rates || {};
  const mat = rr.matriz_actual || {};
  const historico = rr.historico || [];
  const sVar = v => v == null ? "—" : `${v >= 0 ? "+" : ""}${fmt(v, 4)}%`;      // con signo
  const cVar = v => v == null ? COLOR.muted : (v > 0 ? COLOR.red : COLOR.green); // aumento E2/E3 = adverso

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <KpiCard label="Variación Etapa 1"
          value={sVar(mat.var_e1_pct)}
          unit="" accentColor={COLOR.muted} />
        <KpiCard label="Variación Etapa 2"
          value={sVar(mat.var_e2_pct)}
          unit="" accentColor={cVar(mat.var_e2_pct)} />
        <KpiCard label="Variación Etapa 3"
          value={sVar(mat.var_e3_pct)}
          unit="" accentColor={cVar(mat.var_e3_pct)} />
      </div>
      <InsightBox text={rr.narrativa} />
      {historico.length > 0 && (
        <div style={{ marginTop: 14, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse",
                          fontFamily: "monospace", fontSize: 13 }}>
            <thead>
              <tr style={{ background: COLOR.surface }}>
                {["Periodo","Var. E1 %","Var. E2 %","Var. E3 %","E1 (mmdp)","E2 (mmdp)","E3 (mmdp)"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left",
                                       color: COLOR.muted, fontWeight: 600,
                                       borderBottom: `1px solid ${COLOR.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...historico].reverse().slice(0, 12).map((row, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${COLOR.border}22`,
                                     background: i % 2 === 0 ? "transparent" : `${COLOR.surface}80` }}>
                  <td style={{ padding: "7px 12px", color: COLOR.muted }}>
                    {String(row.periodo).slice(0,4)}-{String(row.periodo).slice(4)}
                  </td>
                  <td style={{ padding: "7px 12px", color: COLOR.muted }}>
                    {sVar(row.var_e1_pct)}
                  </td>
                  <td style={{ padding: "7px 12px", color: cVar(row.var_e2_pct) }}>
                    {sVar(row.var_e2_pct)}
                  </td>
                  <td style={{ padding: "7px 12px", color: cVar(row.var_e3_pct) }}>
                    {sVar(row.var_e3_pct)}
                  </td>
                  <td style={{ padding: "7px 12px", color: COLOR.muted }}>{fmtB(row.e1_b, 3)}</td>
                  <td style={{ padding: "7px 12px", color: COLOR.muted }}>{fmtB(row.e2_b, 3)}</td>
                  <td style={{ padding: "7px 12px", color: COLOR.muted }}>{fmtB(row.e3_b, 3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ marginTop: 10, fontSize: 12, color: COLOR.dim, fontFamily: "monospace" }}>
        * Variación neta del saldo de cada etapa sobre su propio saldo del corte anterior (saldos agregados; no es migración por cohortes). Un aumento en Etapa 2 o 3 es adverso (rojo); una disminución es favorable (verde). Los flujos brutos (originación, curas, transiciones, castigos) no son observables con saldos agregados.
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SECCIÓN: POR PRODUCTO
// ══════════════════════════════════════════════════════════════════════
function SeccionProducto({ s }) {
  const prod = s?.producto || {};
  const topSaldo = prod.top_saldo || [];
  const topImor  = prod.top_imor  || [];
  const maxSaldo = topSaldo[0]?.saldo_b || 0.001;
  const maxImor  = topImor[0]?.imor     || 0.001;
  const COLORS   = [COLOR.blue, COLOR.gold, "#a78bfa", COLOR.green, COLOR.amber,
                    COLOR.muted, "#ec4899", "#f97316"];

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 16 }}>
        {/* Top por saldo */}
        <div>
          <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace",
                        textTransform: "uppercase", letterSpacing: ".5px",
                        marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${COLOR.border}` }}>
            Top por saldo
          </div>
          {topSaldo.map((p, i) => (
            <BarRow key={i}
              label={prettyProducto(p.tipo_credito || p.cartera)}
              value={p.saldo_b || 0}
              maxValue={maxSaldo}
              color={COLORS[i] || COLOR.dim}
              rightLabel={`${fmtB(p.saldo_b)} · ${fmtImor(p.imor)}`} />
          ))}
        </div>
        {/* Top por IMOR */}
        <div>
          <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace",
                        textTransform: "uppercase", letterSpacing: ".5px",
                        marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${COLOR.border}` }}>
            Top por IMOR
          </div>
          {topImor.map((p, i) => (
            <BarRow key={i}
              label={prettyProducto(p.tipo_credito || p.cartera)}
              value={p.imor || 0}
              maxValue={maxImor}
              color={p.imor > 5 ? COLOR.red : p.imor > 3 ? COLOR.amber : COLOR.gold}
              rightLabel={fmtImor(p.imor)} />
          ))}
        </div>
      </div>
      <InsightBox text={prod.narrativa} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MAPA MUNICIPIOS CONSUMO (Leaflet canvas)
// ══════════════════════════════════════════════════════════════════════
const GEOJSON_MUN_URL_CON = "/municipios_mx.geojson";
let _cacheMunCon = null, _promMunCon = null;
function fetchGeoMunCon() {
  if (_cacheMunCon) return Promise.resolve(_cacheMunCon);
  if (_promMunCon)  return _promMunCon;
  _promMunCon = fetch(GEOJSON_MUN_URL_CON).then(r => r.json()).then(d => { _cacheMunCon = d; return d; });
  return _promMunCon;
}

function MapaConMunicipio({ municipioData, metrica }) {
  const mapRef     = useRef(null);
  const leafletRef = useRef(null);
  const geoRef     = useRef(null);
  const [geojson, setGeojson] = useState(null);

  useEffect(() => {
    fetchGeoMunCon().then(setGeojson).catch(() => {});
  }, []);

  const dataIdx = React.useMemo(() => {
    if (!municipioData) return {};
    return Object.fromEntries(municipioData.map(d => [d.cvegeo, d]));
  }, [municipioData]);

  const sortedVals = React.useMemo(() => {
    if (!municipioData?.length) return [];
    return municipioData.map(d => d[metrica] || 0).filter(v => v > 0).sort((a, b) => a - b);
  }, [municipioData, metrica]);

  const colorScale = useCallback((val) => {
    if (!val || val <= 0 || !sortedVals.length) return "#1a2535";
    const pct = sortedVals.filter(v => v <= val).length / sortedVals.length;
    const stops = ["#0d2137","#0a3d5c","#0e6188","#1a8fb5","#34d399","#fbbf24","#f87171","#dc2626"];
    const idx = Math.min(Math.floor(pct * stops.length), stops.length - 1);
    return stops[idx];
  }, [sortedVals]);

  useEffect(() => {
    if (!mapRef.current || leafletRef.current) return;
    const L = window.L;
    if (!L) return;
    leafletRef.current = L.map(mapRef.current, {
      center: [23.5, -102], zoom: 5, zoomControl: true,
      attributionControl: false,
    });
    leafletRef.current.getContainer().style.background = "#070c12";
  }, []);

  useEffect(() => {
    const L = window.L;
    if (!L || !leafletRef.current || !geojson || !municipioData) return;
    if (geoRef.current) { geoRef.current.remove(); geoRef.current = null; }
    geoRef.current = L.geoJSON(geojson, {
      style: (feature) => {
        const cv = feature.properties?.CVEGEO || feature.properties?.cvegeo || feature.properties?.CVE_GEO;
        const d  = dataIdx[cv];
        return {
          fillColor:   d ? colorScale(d[metrica]) : "#1a2535",
          fillOpacity: d ? 0.85 : 0.2,
          color: "#0d1520", weight: 0.3,
        };
      },
      onEachFeature: (feature, layer) => {
        const cv = feature.properties?.CVEGEO || feature.properties?.cvegeo || feature.properties?.CVE_GEO;
        const d  = dataIdx[cv];
        if (d) {
          layer.bindTooltip(
            `<b style="font-family:monospace">${d.municipio}, ${d.estado}</b><br/>` +
            `Saldo: ${d.saldo_b?.toFixed(2)} mmdp<br/>` +
            `IMOR: ${d.imor?.toFixed(2)}%<br/>` +
            `Núm. créditos: ${(d.num_creditos || 0).toLocaleString("es-MX")}`,
            { sticky: true }
          );
        }
      },
    }).addTo(leafletRef.current);
  }, [geojson, dataIdx, metrica, colorScale, municipioData]);

  return (
    <div style={{ position: "relative", height: 380 }}>
      {!geojson && (
        <div style={{
          position: "absolute", inset: 0, display: "flex",
          alignItems: "center", justifyContent: "center",
          background: COLOR.bg, color: COLOR.muted, fontSize: 14, fontFamily: "monospace",
        }}>
          Cargando mapa de municipios…
        </div>
      )}
      <div ref={mapRef} style={{ height: "100%", borderRadius: 6, background: COLOR.bg }} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SECCIÓN: TERRITORIO
// ══════════════════════════════════════════════════════════════════════
function SeccionTerritorio({ s }) {
  const [metrica,  setMetrica]  = useState("saldo_b");
  const [subVista, setSubVista] = useState("estado");

  const data = (s?.territorio || []).map(d => ({ ...d, nombre_estado: d.estado }));

  const munRaw   = s?.territorio_municipio;
  const munDatos = Array.isArray(munRaw) ? munRaw : (munRaw?.datos || []);

  const metricas = [
    { key: "saldo_b",   label: "Saldo total"      },
    { key: "vencido_b", label: "Saldo en Etapa 3" },
    { key: "imor",      label: "IMOR"             },
  ];

  const tooltipFn = useCallback((d, nombre) => `
    <div style="font-family:monospace;padding:6px 8px;min-width:180px;background:#0d1520;border:1px solid #1a2535;border-radius:4px">
      <div style="font-size:12px;font-weight:700;color:#dde4ee;margin-bottom:5px">${nombre}</div>
      <div style="font-size:10px;color:#c8a96e;margin-bottom:4px">Saldo: ${fmtB(d?.saldo_b)}</div>
      <div style="font-size:10px;color:#8aa4b8;line-height:1.7">
        Saldo E3: ${fmtB(d?.vencido_b)}<br/>
        IMOR: ${fmtImor(d?.imor)}<br/>
        Núm. créditos: ${fmtN(d?.num_creditos)}
      </div>
    </div>
  `, []);

  const btnStyle = (active) => ({
    padding: "3px 12px", borderRadius: 4, fontSize: 13, fontFamily: "monospace",
    cursor: "pointer", border: `1px solid ${active ? COLOR.gold : COLOR.border}`,
    background: active ? `${COLOR.gold}18` : "none",
    color: active ? COLOR.gold : COLOR.muted,
  });

  const sorted = [...(s?.territorio || [])].sort((a, b) => (b[metrica] || 0) - (a[metrica] || 0));
  const maxVal = sorted[0]?.[metrica] || 1;

  return (
    <div>
      {/* Sub-tabs Estado / Municipio */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        <button onClick={() => setSubVista("estado")}    style={btnStyle(subVista === "estado")}>Estado</button>
        <button onClick={() => setSubVista("municipio")} style={btnStyle(subVista === "municipio")}>Municipio</button>
      </div>

      {/* ── Vista Estado ── */}
      {subVista === "estado" && (
        <>
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            {metricas.map(m => (
              <button key={m.key} onClick={() => setMetrica(m.key)} style={btnStyle(metrica === m.key)}>
                {m.label}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
            <div style={{ flex: "0 0 58%" }}>
              <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace", marginBottom: 6 }}>
                Mapa por Estado — {metricas.find(m => m.key === metrica)?.label}
              </div>
              <div style={{ height: 360, borderRadius: 6, overflow: "hidden", marginBottom: 6 }}>
                <MapaEstados data={data} metrica={metrica} tooltipFn={tooltipFn} />
              </div>
              <MapaLeyenda />
            </div>
            <div style={{ flex: 1, overflowY: "auto", maxHeight: 420 }}>
              <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace",
                            textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 10 }}>
                Ranking — {metricas.find(m => m.key === metrica)?.label}
              </div>
              {sorted.map((item, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between",
                                fontSize: 13, fontFamily: "monospace", marginBottom: 2 }}>
                    <div>
                      <span style={{ color: COLOR.dim, marginRight: 6 }}>{i + 1}</span>
                      <span style={{ color: COLOR.text, fontWeight: 600, textTransform: "uppercase" }}>
                        {item.estado}
                      </span>
                    </div>
                    <span style={{
                      color: metrica === "imor" ? (item.imor > 3 ? COLOR.red : COLOR.gold) : COLOR.gold,
                      fontWeight: 700
                    }}>
                      {metrica === "saldo_b"    ? fmtB(item.saldo_b)
                       : metrica === "vencido_b" ? fmtB(item.vencido_b)
                       : fmtImor(item.imor)}
                    </span>
                  </div>
                  <div style={{ height: 4, background: COLOR.border, borderRadius: 2 }}>
                    <div style={{
                      height: "100%", borderRadius: 2,
                      width: `${Math.min(100, ((item[metrica] || 0) / maxVal) * 100)}%`,
                      background: metrica === "imor"
                        ? (item.imor > 3 ? COLOR.red : COLOR.gold) : COLOR.blue,
                    }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── Vista Municipio ── */}
      {subVista === "municipio" && (
        <div>
          {munDatos.length === 0 ? (
            <div style={{ fontSize: 15, color: COLOR.muted, fontFamily: "monospace", padding: "20px 0" }}>
              Sin datos de municipio disponibles.
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                {metricas.map(m => (
                  <button key={m.key} onClick={() => setMetrica(m.key)} style={btnStyle(metrica === m.key)}>
                    {m.label}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                <div style={{ flex: "0 0 58%" }}>
                  <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace", marginBottom: 6 }}>
                    Mapa Municipal — {metricas.find(m => m.key === metrica)?.label}
                    <span style={{ color: COLOR.dim, marginLeft: 8 }}>
                      ({munDatos.filter(d => d.cvegeo).length} municipios con dato)
                    </span>
                  </div>
                  <MapaConMunicipio municipioData={munDatos} metrica={metrica} />
                  <MapaLeyenda />
                  <div style={{ fontSize: 12, color: COLOR.dim, fontFamily: "monospace", marginTop: 4 }}>
                    Se muestran los municipios con mayor saldo (subconjunto de hasta 500). Un municipio sin color puede tener cartera de consumo y no estar en este subconjunto; no implica que no reporte.
                  </div>
                </div>
                <div style={{ flex: 1, overflowY: "auto", maxHeight: 420 }}>
                  <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace",
                                textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 10 }}>
                    Top municipios — {metricas.find(m => m.key === metrica)?.label}
                  </div>
                  {(() => {
                    const sorted2 = [...munDatos]
                      .filter(d => d.municipio !== "Otros" && (d.saldo_b || 0) >= 0.1)
                      .sort((a, b) => (b[metrica] || 0) - (a[metrica] || 0))
                      .slice(0, 50);
                    const maxV = sorted2[0]?.[metrica] || 1;
                    return sorted2.map((m, i) => (
                      <div key={i} style={{ marginBottom: 6 }}>
                        <div style={{ display: "flex", justifyContent: "space-between",
                                      fontSize: 13, fontFamily: "monospace", marginBottom: 2 }}>
                          <div>
                            <span style={{ color: COLOR.dim, marginRight: 6 }}>{i + 1}</span>
                            <span style={{ color: COLOR.text, fontWeight: 600, textTransform: "uppercase" }}>
                              {m.municipio}
                            </span>
                            <span style={{ color: COLOR.dim, marginLeft: 4 }}>{m.estado}</span>
                          </div>
                          <span style={{
                            color: metrica === "imor" ? (m.imor > 3 ? COLOR.red : COLOR.gold) : COLOR.gold,
                            fontWeight: 700,
                          }}>
                            {metrica === "saldo_b"    ? fmtB(m.saldo_b)
                             : metrica === "vencido_b" ? fmtB(m.vencido_b)
                             : fmtImor(m.imor)}
                          </span>
                        </div>
                        <div style={{ height: 3, background: COLOR.border, borderRadius: 2 }}>
                          <div style={{
                            height: "100%", borderRadius: 2,
                            width: `${Math.min(100, ((m[metrica] || 0) / maxV) * 100)}%`,
                            background: metrica === "imor"
                              ? (m.imor > 3 ? COLOR.red : COLOR.gold) : COLOR.blue,
                          }} />
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SECCIÓN ORIGINACIÓN
// ══════════════════════════════════════════════════════════════════════
// ── helpers de formato para Originación ────────────────────────────────
const _oMdp   = v => v == null ? "—" : `$${Math.round(v).toLocaleString("es-MX")} mdp`;
const _oNum   = v => v == null ? "—" : (Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(2)} M` : v.toLocaleString("es-MX"));
const _oPeso  = v => v == null ? "—" : `$${Math.round(v).toLocaleString("es-MX")}`;
const _oTasa  = v => v == null ? "—" : `${v.toFixed(2)}%`;
const _oPct1  = v => v == null ? "—" : `${v.toFixed(1)}%`;
const _oVarP  = v => v == null ? "n/d" : `${v >= 0 ? "▲ +" : "▼ "}${v.toFixed(1)}%`;
const _oPb    = v => v == null ? "n/d" : `${v >= 0 ? "+" : ""}${v} pb`;
const _operFmt = p => { const s = String(p); return `${s.slice(4)}-${s.slice(0, 4)}`; };

// Matriz burbujas tasa (x) – ticket (y) – monto (tamaño), etiquetada
const _PALETA = [COLOR.blue, COLOR.gold, "#a78bfa", COLOR.green, COLOR.amber, "#ec4899", "#f97316", COLOR.muted];

function _BubbleMatrix({ productos }) {
  const W = 560, H = 340, ML = 58, MB = 42, MT = 40, MR = 20;
  const pts = productos.filter(p => p.tasa != null && p.ticket != null && (p.monto_mdp || 0) > 0)
    .map((p, i) => ({ ...p, _col: _PALETA[i % _PALETA.length] }));
  if (!pts.length) return <div style={{ fontSize: 13, color: COLOR.dim, fontFamily: "monospace" }}>Sin datos</div>;
  const xMax = Math.max(...pts.map(p => p.tasa)) * 1.1 || 1;
  const yMax = Math.max(...pts.map(p => p.ticket)) * 1.18 || 1;
  const mMax = Math.max(...pts.map(p => p.monto_mdp)) || 1;
  const px = v => ML + v / xMax * (W - ML - MR);
  const py = v => H - MB - v / yMax * (H - MB - MT);
  const pr = v => 7 + Math.sqrt(v / mMax) * 26;
  const xticks = [0, 0.25, 0.5, 0.75, 1].map(t => t * xMax);
  const yticks = [0, 0.5, 1].map(t => t * yMax);
  // burbujas grandes al fondo
  const draw = [...pts].sort((a, b) => (b.monto_mdp) - (a.monto_mdp));
  // etiquetas: apiladas verticalmente cuando comparten zona en x (declutter)
  const labs = pts.map(p => ({ p, cx: px(p.tasa), cy: py(p.ticket), r: pr(p.monto_mdp) }))
    .sort((a, b) => a.cx - b.cx);
  let lastX = -999, lastY = -999;
  labs.forEach(l => {
    let y = l.cy - l.r - 7;
    if (Math.abs(l.cx - lastX) < 78 && Math.abs(y - lastY) < 13) y = lastY - 13;
    l.ly = y; lastX = l.cx; lastY = y;
  });
  const short = t => (t || "").length > 22 ? t.slice(0, 21) + "…" : t;
  return (
    <div style={{ overflowX: "auto" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", minWidth: 460, height: "auto" }}>
        {yticks.map((v, i) => (
          <g key={`y${i}`}>
            <line x1={ML} y1={py(v)} x2={W - MR} y2={py(v)} stroke={COLOR.border} strokeWidth="0.5" />
            <text x={ML - 6} y={py(v) + 3} textAnchor="end" fontSize="9" fill={COLOR.dim} fontFamily="monospace">${Math.round(v / 1000)}K</text>
          </g>
        ))}
        {xticks.map((v, i) => (
          <text key={`x${i}`} x={px(v)} y={H - MB + 14} textAnchor="middle" fontSize="9" fill={COLOR.dim} fontFamily="monospace">{v.toFixed(0)}%</text>
        ))}
        <text x={(ML + W - MR) / 2} y={H - 5} textAnchor="middle" fontSize="10" fill={COLOR.muted} fontFamily="monospace">Tasa ponderada</text>
        <text x={13} y={(MT + H - MB) / 2} textAnchor="middle" fontSize="10" fill={COLOR.muted} fontFamily="monospace" transform={`rotate(-90 13 ${(MT + H - MB) / 2})`}>Ticket promedio</text>
        {draw.map((p, i) => (
          <circle key={i} cx={px(p.tasa)} cy={py(p.ticket)} r={pr(p.monto_mdp)}
            fill={p._col} fillOpacity="0.3" stroke={p._col} strokeWidth="1.3" />
        ))}
        {labs.map((l, i) => (
          <g key={i}>
            <line x1={l.cx} y1={l.cy - l.r} x2={l.cx} y2={l.ly + 2} stroke={l.p._col} strokeWidth="0.6" opacity="0.6" />
            <text x={l.cx} y={l.ly} textAnchor="middle" fontSize="9.5" fill={COLOR.text} fontFamily="monospace">{short(l.p.producto)}</text>
          </g>
        ))}
      </svg>
      <div style={{ fontSize: 9, color: COLOR.dim, fontFamily: "monospace", marginTop: 4 }}>
        Tamaño de burbuja ∝ monto originado.
      </div>
    </div>
  );
}

// Encabezado de sección en forma de pregunta (estilo maqueta)
function _tituloPreg(txt, extra) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline",
                  margin: "0 0 12px", paddingBottom: 8, borderBottom: `1px solid ${COLOR.border}` }}>
      <span style={{ fontSize: 15, fontWeight: 600, color: COLOR.text, fontFamily: "'DM Sans',system-ui,sans-serif" }}>{txt}</span>
      {extra ? <span style={{ fontSize: 11, color: COLOR.dim, fontFamily: "monospace" }}>{extra}</span> : null}
    </div>
  );
}

// Mini panel de evolución (métrica + sparkline + subtítulo)
function _MiniPanel({ label, valor, data, accessor, mm, color, sub }) {
  const W = 260, H = 46, P = 3;
  const vals = (data || []).map(accessor);
  let poly = "", mmpoly = "";
  if (vals.length > 1) {
    const min = Math.min(...vals), max = Math.max(...vals), rng = (max - min) || 1;
    const x = i => P + i / (vals.length - 1) * (W - 2 * P);
    const y = v => H - P - (v - min) / rng * (H - 2 * P);
    poly = vals.map((v, i) => `${x(i)},${y(v)}`).join(" ");
    if (mm) mmpoly = data.map((d, i) => `${x(i)},${y(mm(d))}`).join(" ");
  }
  return (
    <div style={{ background: COLOR.surface, border: `1px solid ${COLOR.border}`, borderRadius: 6, padding: "10px 12px" }}>
      <div style={{ fontSize: 10, color: COLOR.muted, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: ".5px" }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color: COLOR.gold, fontFamily: "monospace", margin: "2px 0 4px" }}>{valor}</div>
      {poly ? (
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 40 }} preserveAspectRatio="none">
          {mmpoly && <polyline points={mmpoly} fill="none" stroke={COLOR.dim} strokeWidth="1" strokeDasharray="3 3" opacity="0.6" />}
          <polyline points={poly} fill="none" stroke={color} strokeWidth="1.5" />
        </svg>
      ) : <div style={{ height: 40 }} />}
      <div style={{ fontSize: 9, color: COLOR.dim, fontFamily: "monospace" }}>{sub || "24 periodos históricos"}</div>
    </div>
  );
}

// Tarjeta de segmento con borde de color (estilo "¿A quién y dónde?")
function _SegCard({ titulo, color, children }) {
  return (
    <div style={{ background: COLOR.surface, border: `1px solid ${COLOR.border}`, borderLeft: `3px solid ${color}`,
                  borderRadius: 6, padding: "10px 14px" }}>
      <div style={{ fontSize: 10, color: COLOR.muted, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 4 }}>{titulo}</div>
      <div style={{ fontSize: 12, color: COLOR.text, lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

const _thO = { padding: "6px 8px", textAlign: "right", color: COLOR.muted, fontWeight: 600,
               borderBottom: `1px solid ${COLOR.border}`, fontSize: 10.5, fontFamily: "monospace", whiteSpace: "nowrap" };
const _tdO = { padding: "5px 8px", textAlign: "right", fontSize: 11.5, fontFamily: "monospace", color: COLOR.text, whiteSpace: "nowrap" };

function SeccionOriginacion({ s }) {
  const orig = s?.originacion || {};
  const total = orig.total || {};
  const hist  = orig.historico || [];
  const productos = orig.productos || [];
  const desc  = orig.descomposicion_tasa || {};
  const inst  = orig.instituciones || [];
  const conc  = orig.concentracion;
  const seg   = orig.segmentos || {};
  const cal   = orig.calidad_inicial || {};
  const cdatos = orig.calidad_datos || [];
  const cm    = orig.contexto_mercado || {};

  if (!total.monto_mdp && productos.length === 0) {
    return <div style={{ fontSize: 14, color: COLOR.dim, fontFamily: "monospace" }}>Sin datos de originación para el corte.</div>;
  }

  const vc = total.var_corte_anterior || {};
  const va = total.var_anual || {};
  const perTxt = orig.periodo ? _operFmt(orig.periodo) : "";
  const alcanceTxt = orig.alcance === "institucional" ? "Institución" : "Banca múltiple";
  const genAgg = seg.genero?.agregado || [];
  const secArr = seg.sector_laboral || [];
  const noAsal = secArr.find(r => /no *asal/i.test(r.clave || ""));
  const muj = genAgg.find(g => /^m/i.test((g.genero || "").trim()));
  const geo = seg.geografia || {};
  const geoTop3 = (geo.top5 || []).slice(0, 3);
  const geoTop3Pct = Math.round(geoTop3.reduce((a, r) => a + (r.pct_monto || 0), 0) * 10) / 10;
  const penTop = geo.penetracion_top || [];
  const ingCero = (seg.ingreso?.filas || []).find(r => /cero|no informado/i.test(r.intervalo || ""));
  const hhiTop = (conc?.hhi_por_producto || []).slice(0, 3);
  const _sv = v => v == null ? COLOR.muted : v >= 0 ? COLOR.green : COLOR.red;
  const _vp = v => v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;

  const Kpi = ({ label, valor, unidad, cCorte, cAnual, pb }) => {
    const f = pb ? (v => v == null ? "—" : `${v >= 0 ? "+" : ""}${v} pb`) : _vp;
    return (
      <div style={{ background: COLOR.surface, border: `1px solid ${COLOR.border}`, borderRadius: 8, padding: "12px 16px" }}>
        <div style={{ fontSize: 10.5, color: COLOR.muted, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: ".5px" }}>{label}</div>
        <div style={{ fontSize: 23, fontWeight: 800, color: COLOR.gold, fontFamily: "monospace", margin: "3px 0" }}>
          {valor}{unidad ? <span style={{ fontSize: 12, color: COLOR.muted, fontWeight: 400 }}> {unidad}</span> : null}
        </div>
        <div style={{ fontSize: 10.5, color: COLOR.dim, fontFamily: "monospace" }}>
          Corte anterior <span style={{ color: _sv(cCorte) }}>{f(cCorte)}</span> · Anual <span style={{ color: _sv(cAnual) }}>{f(cAnual)}</span>
        </div>
      </div>
    );
  };

  return (
    <div style={{ fontFamily: "'DM Sans',system-ui,sans-serif" }}>
      {/* Encabezado + Lectura del mes */}
      <div style={{ display: "grid", gridTemplateColumns: "1.25fr 1fr", gap: 24, alignItems: "start", marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: COLOR.text }}>Originación de cartera de consumo</div>
          <div style={{ fontSize: 12, color: COLOR.dim, fontFamily: "monospace", marginTop: 3 }}>
            {alcanceTxt} · {perTxt} · créditos marginales · cortes {orig.periodicidad || "—"}
          </div>
        </div>
        {orig.narrativa && (
          <div style={{ fontSize: 11.5, color: COLOR.muted, lineHeight: 1.6 }}>
            <span style={{ color: COLOR.gold, fontWeight: 700, fontFamily: "monospace", fontSize: 10.5 }}>LECTURA DEL MES. </span>
            {orig.narrativa}
          </div>
        )}
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
        <Kpi label="Monto originado"    valor={total.monto_mdp?.toLocaleString("es-MX")} unidad="mdp" cCorte={vc.monto} cAnual={va.monto} />
        <Kpi label="Créditos otorgados" valor={_oNum(total.numero_creditos)}             unidad="créditos" cCorte={vc.creditos} cAnual={va.creditos} />
        <Kpi label="Ticket promedio"    valor={_oPeso(total.ticket_promedio)}            cCorte={vc.ticket} cAnual={va.ticket} />
        <Kpi label="Tasa ponderada"     valor={_oTasa(total.tasa_ponderada)}            cCorte={vc.tasa_pb} cAnual={va.tasa_pb} pb />
      </div>

      {/* Dos columnas */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.15fr) minmax(0,0.85fr)", gap: 28, alignItems: "start" }}>

        {/* ── Columna izquierda ── */}
        <div>
          {_tituloPreg("¿La originación crece por volumen, ticket o precio?")}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 26 }}>
            <_MiniPanel label="Monto originado"    valor={_oMdp(total.monto_mdp)}        data={hist} accessor={d => d.monto_mdp} mm={d => d.mm3_monto}    color={COLOR.gold} />
            <_MiniPanel label="Número de créditos" valor={_oNum(total.numero_creditos)} data={hist} accessor={d => d.creditos}  mm={d => d.mm3_creditos} color={COLOR.blue} />
            <_MiniPanel label="Ticket promedio"    valor={_oPeso(total.ticket_promedio)} data={hist} accessor={d => d.ticket}                          color={COLOR.green} />
            <_MiniPanel label="Tasa ponderada"     valor={_oTasa(total.tasa_ponderada)}  data={hist} accessor={d => d.tasa}                            color="#a78bfa" />
          </div>

          {_tituloPreg("¿Qué productos explican la originación?")}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={{ ..._thO, textAlign: "left" }}>Producto</th>
                <th style={_thO}>Monto mdp</th><th style={_thO}>% monto</th><th style={_thO}>Créditos</th>
                <th style={_thO}>Ticket</th><th style={_thO}>Tasa</th><th style={_thO}>Var. anual</th><th style={_thO}>Δ tasa</th>
              </tr></thead>
              <tbody>
                {productos.map((p, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${COLOR.border}33` }}>
                    <td style={{ ..._tdO, textAlign: "left", color: i === 0 ? COLOR.blue : COLOR.text, whiteSpace: "normal" }}>{p.producto}</td>
                    <td style={_tdO}>{p.monto_mdp?.toLocaleString("es-MX")}</td>
                    <td style={{ ..._tdO, color: COLOR.muted }}>{_oPct1(p.pct_monto)}</td>
                    <td style={_tdO}>{_oNum(p.creditos)}</td>
                    <td style={_tdO}>{_oPeso(p.ticket)}</td>
                    <td style={{ ..._tdO, color: COLOR.gold }}>{_oTasa(p.tasa)}</td>
                    <td style={{ ..._tdO, color: _sv(p.var_anual_monto) }}>{_oVarP(p.var_anual_monto)}</td>
                    <td style={{ ..._tdO, color: p.var_anual_tasa_pb == null ? COLOR.muted : p.var_anual_tasa_pb >= 0 ? COLOR.red : COLOR.green }}>{_oPb(p.var_anual_tasa_pb)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {desc.delta_total_pb != null && (
            <div style={{ marginTop: 10, fontSize: 11.5, color: COLOR.muted, lineHeight: 1.55 }}>
              <span style={{ color: COLOR.gold, fontWeight: 600 }}>Descomposición de la tasa:</span>{" "}
              total {_oPb(desc.delta_total_pb)} = efecto precio {_oPb(desc.efecto_precio_pb)} (tasas dentro de cada producto) + efecto mezcla {_oPb(desc.efecto_mezcla_pb)} (peso de productos caros/baratos).
            </div>
          )}

          {inst.length > 0 && <>
            <div style={{ height: 26 }} />
            {_tituloPreg("¿Quién está colocando el crédito?")}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={{ ..._thO, textAlign: "left" }}>Institución</th>
                  <th style={_thO}>Monto mdp</th><th style={_thO}>% monto</th><th style={_thO}>% créd.</th>
                  <th style={_thO}>Ticket</th><th style={_thO}>Tasa</th>
                </tr></thead>
                <tbody>
                  {inst.map((e, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${COLOR.border}33`,
                                         background: e.institucion === "Otros" ? `${COLOR.surface}80` : "transparent" }}>
                      <td style={{ ..._tdO, textAlign: "left", color: i === 0 ? COLOR.blue : COLOR.text }}>{e.institucion}</td>
                      <td style={_tdO}>{e.monto_mdp?.toLocaleString("es-MX")}</td>
                      <td style={{ ..._tdO, color: COLOR.muted }}>{_oPct1(e.pct_monto)}</td>
                      <td style={{ ..._tdO, color: COLOR.muted }}>{_oPct1(e.pct_creditos)}</td>
                      <td style={_tdO}>{e.ticket != null ? _oPeso(e.ticket) : "—"}</td>
                      <td style={{ ..._tdO, color: COLOR.gold }}>{e.tasa != null ? _oTasa(e.tasa) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {conc && (
              <div style={{ marginTop: 10, fontSize: 11.5, color: COLOR.muted, lineHeight: 1.55 }}>
                Concentración por monto: <b style={{ color: COLOR.text }}>HHI {conc.hhi_monto}</b>; las cuatro mayores instituciones reúnen <b style={{ color: COLOR.text }}>{conc.cr4_monto}%</b>. Por número de créditos, HHI <b style={{ color: COLOR.text }}>{conc.hhi_creditos}</b>
                {conc.cambio_hhi_monto != null && <> (Δ HHI monto {conc.cambio_hhi_monto >= 0 ? "+" : ""}{conc.cambio_hhi_monto})</>}.
              </div>
            )}
            {hhiTop.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: `repeat(${hhiTop.length}, 1fr)`, gap: 10, marginTop: 12 }}>
                {hhiTop.map((h, i) => (
                  <div key={i} style={{ background: COLOR.surface, border: `1px solid ${COLOR.border}`, borderRadius: 6, padding: "8px 12px", textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: COLOR.muted, fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{h.producto}</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: COLOR.gold, fontFamily: "monospace", marginTop: 2 }}>HHI {h.hhi?.toLocaleString("es-MX")}</div>
                    <div style={{ fontSize: 9, color: COLOR.dim, fontFamily: "monospace" }}>{h.lider} {_oPct1(h.part_lider)}</div>
                  </div>
                ))}
              </div>
            )}
          </>}
        </div>

        {/* ── Columna derecha ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <div>
            {_tituloPreg("Contexto de mercado")}
            <div style={{ background: COLOR.surface, border: `1px solid ${COLOR.border}`, borderRadius: 6, overflow: "hidden" }}>
              {[["Confianza del consumidor",
                 cm.icc?.valor != null ? `${cm.icc.valor}` : "Configurar ICC",
                 cm.icc?.valor != null ? COLOR.gold : COLOR.dim],
                ["Variación anual del ICC",
                 cm.icc?.var_anual != null ? `${cm.icc.var_anual >= 0 ? "+" : ""}${cm.icc.var_anual} pts` : "— pts",
                 cm.icc?.var_anual == null ? COLOR.dim : cm.icc.var_anual >= 0 ? COLOR.green : COLOR.red],
                ["Tasa objetivo (Banxico)",
                 cm.tasa_referencia != null ? `${cm.tasa_referencia.toFixed(2)}%` : "—",
                 cm.tasa_referencia != null ? COLOR.gold : COLOR.dim],
                ["Spread originación–tasa obj.",
                 cm.spread_pb != null ? `${cm.spread_pb >= 0 ? "+" : ""}${cm.spread_pb} pb` : "—",
                 cm.spread_pb != null ? COLOR.text : COLOR.dim]].map(([l, v, c], i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 14px",
                                      borderBottom: i < 3 ? `1px solid ${COLOR.border}` : "none", fontSize: 11.5, fontFamily: "monospace" }}>
                  <span style={{ color: COLOR.text }}>{l}</span>
                  <span style={{ color: c }}>{v}</span>
                </div>
              ))}
            </div>
            {desc.delta_total_pb != null && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 10 }}>
                {[["Efecto precio", desc.efecto_precio_pb], ["Efecto mezcla", desc.efecto_mezcla_pb], ["Cambio total", desc.delta_total_pb]].map(([l, v], i) => (
                  <div key={i} style={{ background: COLOR.surface, border: `1px solid ${COLOR.border}`, borderRadius: 6, padding: "8px 10px", textAlign: "center" }}>
                    <div style={{ fontSize: 9.5, color: COLOR.muted, fontFamily: "monospace", textTransform: "uppercase" }}>{l}</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: COLOR.gold, fontFamily: "monospace" }}>{v >= 0 ? "+" : ""}{v} pb</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            {_tituloPreg("¿Ticket o precio?")}
            <_BubbleMatrix productos={productos} />
          </div>

          <div>
            {_tituloPreg("¿A quién y dónde?")}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {noAsal && (
                <_SegCard titulo="Sector laboral" color={COLOR.blue}>
                  No asalariados: {_oPct1(noAsal.pct_creditos)} de los créditos, pero {_oPct1(noAsal.pct_monto)} del monto. Ticket {_oPeso(noAsal.ticket)} y tasa {_oTasa(noAsal.tasa)}.
                </_SegCard>
              )}
              {muj && (
                <_SegCard titulo="Género" color={COLOR.blue}>
                  Mujeres: {_oPct1(muj.pct_creditos)} de créditos y {_oPct1(muj.pct_monto)} del monto. La brecha de tasa agregada depende de la mezcla de productos.
                </_SegCard>
              )}
              {geoTop3.length > 0 && (
                <_SegCard titulo="Geografía" color={COLOR.blue}>
                  {geoTop3.map(r => r.estado).join(", ")} concentran {_oPct1(geoTop3Pct)} del monto. HHI estatal {geo.hhi_territorial ?? "—"}.
                  {penTop[0] && ` Mayor penetración: ${penTop[0].estado} (${penTop[0].pen_monto} mdp por 1,000 hab).`}
                </_SegCard>
              )}
              {penTop.length > 0 && (
                <_SegCard titulo="Penetración por 1,000 habitantes" color={COLOR.blue}>
                  <div style={{ fontSize: 10, color: COLOR.dim, marginBottom: 4 }}>Población total, Censo 2020 (INEGI)</div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr>
                      <th style={{ ..._thO, textAlign: "left" }}>Estado</th>
                      <th style={_thO}>mdp/1k hab</th><th style={_thO}>créd./1k hab</th>
                    </tr></thead>
                    <tbody>
                      {penTop.map((r, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${COLOR.border}22` }}>
                          <td style={{ ..._tdO, textAlign: "left", color: COLOR.text }}>{r.estado}</td>
                          <td style={{ ..._tdO, color: COLOR.gold }}>{r.pen_monto}</td>
                          <td style={{ ..._tdO, color: COLOR.muted }}>{r.pen_creditos}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </_SegCard>
              )}
              {cal.part_e2e3 != null && (
                <_SegCard titulo="Calidad inicial" color={COLOR.red}>
                  {_oTasa(cal.part_e2e3)} del monto aparece en etapas 2 o 3{cal.cambio_corte_anterior_pb != null && ` (${_oPb(cal.cambio_corte_anterior_pb)} vs. corte anterior)`}. No se asume deterioro: puede haber refinanciamientos o reclasificaciones.
                  {cal.valor_razonable_mdp ? ` Valor razonable: ${_oMdp(cal.valor_razonable_mdp)}.` : ""}
                </_SegCard>
              )}
              {ingCero && (
                <_SegCard titulo="Cobertura del dato" color={COLOR.amber}>
                  Ingreso “0”: {_oPct1(ingCero.pct_creditos)} de créditos y {_oPct1(ingCero.pct_monto)} del monto (cobertura {_oPct1(seg.ingreso?.cobertura_creditos)}). Validar si significa cero, no informado o no aplicable.
                </_SegCard>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Validaciones y elementos pendientes */}
      <div style={{ marginTop: 28 }}>
        {_tituloPreg("Validaciones y elementos pendientes de conexión")}
        <div style={{ background: COLOR.surface, border: `1px solid ${COLOR.border}`, borderRadius: 8, padding: "14px 18px" }}>
          {cdatos.map((c, i) => (
            <div key={`v${i}`} style={{ fontSize: 12, color: COLOR.muted, lineHeight: 1.9, display: "flex", gap: 8 }}>
              <span style={{ color: COLOR.red }}>●</span><span>{c.detalle}</span>
            </div>
          ))}
          {[
            "Alinear Confianza del Consumidor y TIIE de Fondeo desde las APIs de Banxico; medir rezagos sin afirmar causalidad (Fase 2).",
            "Penetración: se usa población TOTAL (Censo 2020, INEGI) por cada 1,000 habitantes. Para 18+ estricto, cargar tabla INEGI de población adulta.",
            "Usar siempre SUM(tasa_por_saldo) / SUM(saldo); nunca promediar tasas ya agregadas.",
          ].map((t, i) => (
            <div key={`p${i}`} style={{ fontSize: 12, color: COLOR.muted, lineHeight: 1.9, display: "flex", gap: 8 }}>
              <span style={{ color: COLOR.gold }}>●</span><span>{t}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SECCIÓN RIESGO (HHI + Anomalías)
// ══════════════════════════════════════════════════════════════════════
const HHI_DESC_CON = `El Índice Herfindahl-Hirschman (HHI) mide la concentración de mercado sumando el cuadrado de las participaciones de cada institución dentro de cada segmento de producto. Escala: 0–10,000. Los cortes < 1,500 / 1,500–2,500 / > 2,500 (competitivo / moderado / alto) son criterios internos de monitoreo de este reporte, no una clasificación oficial ni universal de riesgo.`;

const ANOM_DESC_CON = `Detección de cambios ATÍPICOS del IMOR por z-score del delta bimestral. Para cada par (institución, tipo de cartera) con saldo ≥ 100 MDP, se calcula la media y desviación estándar histórica del cambio bimestral en IMOR; se reportan los casos que superan 2 desviaciones estándar y un delta absoluto ≥ 0.5 pp. El z-score mide la RAREZA ESTADÍSTICA del cambio (Z > 0 = alza inusual del IMOR; Z < 0 = baja inusual), NO su severidad financiera: la materialidad debe juzgarse con el saldo afectado, la persistencia y la magnitud del IMOR. Un IMOR ≈ 0 puede indicar no-reporte o salida de cartera y debe verificarse antes de interpretarse como mejora.`;

function SeccionRiesgo({ s }) {
  const conc = s?.concentracion || {};
  const anom = s?.anomalias     || {};

  const numAnom = (anom.filas || []).filter(f => f.z_score > 0).length;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
      {/* ── HHI ── */}
      <div>
        <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace",
                      textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8 }}>
          Concentración HHI
        </div>
        <div style={{ fontSize: 13, color: COLOR.dim, fontFamily: "monospace", lineHeight: 1.6,
                      marginBottom: 10, background: COLOR.surface, border: `1px solid ${COLOR.border}`,
                      borderRadius: 4, padding: "6px 10px" }}>
          {HHI_DESC_CON}
        </div>
        {conc.insight && (
          <div style={{ fontSize: 14, color: COLOR.muted, lineHeight: 1.6, marginBottom: 10 }}>
            {conc.insight}
          </div>
        )}
        {(conc.filas || []).map((f, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between",
                                padding: "5px 0", borderBottom: `1px solid ${COLOR.border}33`,
                                fontSize: 14, fontFamily: "monospace" }}>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "55%" }}>
              <span style={{ color: COLOR.text }}>{f.descripcion_estado}</span>
              {f.banco_dominante && (
                <span style={{ color: COLOR.dim, marginLeft: 6, fontSize: 13 }}>
                  · {f.banco_dominante} {f.cuota_dominante_pct?.toFixed(0)}%
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {f.imor_segmento != null && (
                <span style={{ fontSize: 13, color: f.imor_segmento > 5 ? COLOR.red : COLOR.muted }}>
                  IMOR {f.imor_segmento?.toFixed(1)}%
                </span>
              )}
              <span style={{
                color: f.categoria_hhi === "Alta" ? COLOR.red
                     : f.categoria_hhi === "Moderada" ? COLOR.gold : COLOR.green,
                fontWeight: 700,
              }}>
                HHI {Math.round(f.hhi_actual)}
                {f.delta_hhi != null && f.delta_hhi !== 0 && (
                  <span style={{ fontSize: 12, marginLeft: 4,
                                 color: f.delta_hhi > 0 ? COLOR.red : COLOR.green }}>
                    {f.delta_hhi > 0 ? "▲" : "▼"}
                  </span>
                )}
              </span>
            </div>
          </div>
        ))}
        {!conc.filas?.length && (
          <div style={{ fontSize: 14, color: COLOR.dim, fontFamily: "monospace" }}>
            Sin datos de concentración
          </div>
        )}
      </div>

      {/* ── Anomalías ── */}
      <div>
        <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace",
                      textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8 }}>
          Anomalías Estadísticas
          {numAnom > 0 && (
            <span style={{ marginLeft: 8, background: COLOR.red, color: "#fff",
                           borderRadius: 10, padding: "1px 7px", fontSize: 12 }}>
              {numAnom}
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, color: COLOR.dim, fontFamily: "monospace", lineHeight: 1.6,
                      marginBottom: 10, background: COLOR.surface, border: `1px solid ${COLOR.border}`,
                      borderRadius: 4, padding: "6px 10px" }}>
          {ANOM_DESC_CON}
        </div>
        {(anom.filas || []).slice(0, 8).map((a, i) => (
          <div key={i} style={{ padding: "6px 0", borderBottom: `1px solid ${COLOR.border}33` }}>
            <div style={{ fontSize: 14, color: COLOR.text, fontFamily: "monospace", marginBottom: 2 }}>
              {a.nombre_entidad}
            </div>
            <div style={{ fontSize: 13, color: COLOR.dim, fontFamily: "monospace", marginBottom: 2 }}>
              {a.actividad_economica}
            </div>
            <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace" }}>
              IMOR {fmtImor(a.imor)}
              {" · "}Δ {a.delta_imor_pp != null
                ? `${a.delta_imor_pp > 0 ? "+" : ""}${fmt(a.delta_imor_pp, 2)} pp`
                : "—"}
              {" · "}z={fmt(a.z_score, 1)}
              {" · "}
              <span style={{
                color: (a.tipo_anomalia || "").includes("verificar") ? COLOR.amber
                     : a.z_score > 2.5 ? COLOR.red
                     : a.z_score > 2   ? COLOR.amber
                     : a.z_score < -2  ? COLOR.green : COLOR.muted,
                fontWeight: 700,
              }}>
                {a.tipo_anomalia}
              </span>
            </div>
          </div>
        ))}
        {!anom.filas?.length && (
          <div style={{ fontSize: 14, color: COLOR.dim, fontFamily: "monospace" }}>
            Sin anomalías de IMOR relevantes en el periodo.
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// GRÁFICA FORECAST CONSUMO (saldo + IMOR eje doble)
// ══════════════════════════════════════════════════════════════════════
function ForecastChartCon({ saldoHist, saldoProy, imorHist, imorProy }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    const PAD = { top: 24, right: 60, bottom: 36, left: 72 };
    ctx.clearRect(0, 0, W, H);

    const allPeriodos = [
      ...saldoHist.map(d => d.periodo),
      ...saldoProy.map(d => d.periodo),
    ].filter((v, i, a) => a.indexOf(v) === i).sort();

    if (!allPeriodos.length) return;

    const saldoMap = Object.fromEntries(saldoHist.map(d => [d.periodo, d.saldo_b]));
    const saldoPMap = Object.fromEntries(saldoProy.map(d => [d.periodo, d.valor / 1e9]));
    const imorMap  = Object.fromEntries(imorHist.map(d => [d.periodo, d.imor]));
    const imorPMap = Object.fromEntries(imorProy.map(d => [d.periodo, d.valor]));

    const allSaldo = [...Object.values(saldoMap), ...Object.values(saldoPMap)].filter(Boolean);
    const allImor  = [...Object.values(imorMap),  ...Object.values(imorPMap) ].filter(Boolean);
    const maxS = Math.max(...allSaldo) * 1.1 || 1;
    const maxI = Math.max(...allImor)  * 1.2 || 1;

    const xOf = i => PAD.left + i * (W - PAD.left - PAD.right) / (allPeriodos.length - 1 || 1);
    const yS  = v => PAD.top + (1 - v / maxS) * (H - PAD.top - PAD.bottom);
    const yI  = v => PAD.top + (1 - v / maxI) * (H - PAD.top - PAD.bottom);

    const lastHistIdx = allPeriodos.findIndex(p => !saldoMap[p] && saldoPMap[p] !== undefined) - 1;

    // Grid
    ctx.strokeStyle = "#1a2535"; ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      const y = PAD.top + i * (H - PAD.top - PAD.bottom) / 4;
      ctx.beginPath(); ctx.moveTo(PAD.left, y); ctx.lineTo(W - PAD.right, y); ctx.stroke();
    }

    // Zona proyección
    if (lastHistIdx >= 0) {
      ctx.fillStyle = "#1a8fb508";
      ctx.fillRect(xOf(lastHistIdx + 1), PAD.top, W - PAD.right - xOf(lastHistIdx + 1), H - PAD.top - PAD.bottom);
    }

    // Saldo histórico
    ctx.strokeStyle = "#1a8fb5"; ctx.lineWidth = 2; ctx.beginPath();
    allPeriodos.forEach((p, i) => {
      if (saldoMap[p] != null) { i === 0 || !saldoMap[allPeriodos[i-1]] ? ctx.moveTo(xOf(i), yS(saldoMap[p])) : ctx.lineTo(xOf(i), yS(saldoMap[p])); }
    });
    ctx.stroke();

    // Saldo proyectado
    ctx.strokeStyle = "#1a8fb5"; ctx.lineWidth = 2; ctx.setLineDash([5, 3]); ctx.beginPath();
    let first = true;
    allPeriodos.forEach((p, i) => {
      if (saldoPMap[p] != null) { first ? ctx.moveTo(xOf(i), yS(saldoPMap[p])) : ctx.lineTo(xOf(i), yS(saldoPMap[p])); first = false; }
    });
    ctx.stroke(); ctx.setLineDash([]);

    // IMOR histórico
    ctx.strokeStyle = "#f59e0b"; ctx.lineWidth = 2; ctx.beginPath();
    allPeriodos.forEach((p, i) => {
      if (imorMap[p] != null) { i === 0 || !imorMap[allPeriodos[i-1]] ? ctx.moveTo(xOf(i), yI(imorMap[p])) : ctx.lineTo(xOf(i), yI(imorMap[p])); }
    });
    ctx.stroke();

    // IMOR proyectado
    ctx.strokeStyle = "#f59e0b"; ctx.lineWidth = 2; ctx.setLineDash([5, 3]); ctx.beginPath();
    first = true;
    allPeriodos.forEach((p, i) => {
      if (imorPMap[p] != null) { first ? ctx.moveTo(xOf(i), yI(imorPMap[p])) : ctx.lineTo(xOf(i), yI(imorPMap[p])); first = false; }
    });
    ctx.stroke(); ctx.setLineDash([]);

    // Ejes y etiquetas
    ctx.fillStyle = "#8aa4b8"; ctx.font = "9px monospace"; ctx.textAlign = "right";
    for (let i = 0; i <= 4; i++) {
      const v = maxS * (1 - i / 4);
      ctx.fillText(`${v.toFixed(0)}`, PAD.left - 4, PAD.top + i * (H - PAD.top - PAD.bottom) / 4 + 3);
    }
    ctx.textAlign = "left";
    for (let i = 0; i <= 4; i++) {
      const v = maxI * (1 - i / 4);
      ctx.fillText(`${v.toFixed(2)}%`, W - PAD.right + 4, PAD.top + i * (H - PAD.top - PAD.bottom) / 4 + 3);
    }
    ctx.textAlign = "center";
    allPeriodos.filter((_, i) => i % Math.ceil(allPeriodos.length / 8) === 0).forEach(p => {
      const i = allPeriodos.indexOf(p);
      ctx.fillText(`${String(p).slice(0,4)}-${String(p).slice(4)}`, xOf(i), H - 4);
    });

    // Leyenda
    ctx.fillStyle = "#1a8fb5"; ctx.fillRect(PAD.left, 6, 20, 3);
    ctx.fillStyle = "#8aa4b8"; ctx.textAlign = "left"; ctx.font = "9px monospace";
    ctx.fillText("Saldo (mmdp)", PAD.left + 24, 10);
    ctx.fillStyle = "#f59e0b"; ctx.fillRect(PAD.left + 110, 6, 20, 3);
    ctx.fillText("IMOR (%)", PAD.left + 134, 10);
    ctx.fillStyle = "#8aa4b888"; ctx.fillRect(PAD.left + 200, 6, 20, 3);
    ctx.fillText("Proyección", PAD.left + 224, 10);
  }, [saldoHist, saldoProy, imorHist, imorProy]);

  return (
    <canvas ref={canvasRef} width={820} height={200}
      style={{ width: "100%", height: 200, display: "block", marginBottom: 16 }} />
  );
}

function SeccionPronostico({ s }) {
  const fc = s?.forecast || {};

  if (!fc.cubierto) {
    return (
      <div style={{ fontSize: 15, color: COLOR.muted, fontFamily: "monospace", padding: "20px 0" }}>
        {fc.advertencia || "Modelos ARIMA no disponibles para esta selección."}
        {fc.error && <div style={{ color: COLOR.red, marginTop: 8, fontSize: 13 }}>{fc.error}</div>}
      </div>
    );
  }

  const saldoData = fc.saldo || {};
  const imorData  = fc.imor  || {};
  const proy_s    = saldoData.proyecciones || [];
  const proy_i    = imorData.proyecciones  || [];
  const hist      = saldoData.historico    || [];
  const excluidas = imorData.excluidas     || [];

  const PanelForecast = ({ titulo, proyecciones, unidad, decimales, insight, advertencia }) => (
    <div style={{ background: COLOR.surface, border: `1px solid ${COLOR.border}`,
                  borderRadius: 6, padding: 16 }}>
      <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace",
                    textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 12 }}>
        {titulo}
      </div>
      {advertencia && (
        <div style={{ fontSize: 13, color: COLOR.gold, fontFamily: "monospace",
                      background: `${COLOR.gold}0d`, border: `1px solid ${COLOR.border}`,
                      borderRadius: 4, padding: "5px 8px", marginBottom: 10 }}>
          ⚠ {advertencia}
        </div>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14,
                      fontFamily: "monospace", marginBottom: 12 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${COLOR.border}` }}>
            {["Periodo", "Proyección", "IC Bajo (90%)", "IC Alto (90%)"].map(h => (
              <th key={h} style={{ padding: "4px 8px", color: COLOR.muted,
                                   fontWeight: 600, textAlign: "right" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {proyecciones.map((p, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${COLOR.border}33` }}>
              <td style={{ padding: "4px 8px", color: COLOR.gold, textAlign: "right" }}>
                {`${String(p.periodo).slice(0,4)}-${String(p.periodo).slice(4)}`}
              </td>
              <td style={{ padding: "4px 8px", color: COLOR.text, textAlign: "right", fontWeight: 600 }}>
                {unidad === "mmdp"
                  ? `${(p.valor / 1e9).toFixed(decimales)} mmdp`
                  : `${fmt(p.valor, decimales)}%`}
              </td>
              <td style={{ padding: "4px 8px", color: COLOR.muted, textAlign: "right" }}>
                {p.lower != null
                  ? (unidad === "mmdp" ? `${(p.lower / 1e9).toFixed(decimales)}` : `${fmt(p.lower, decimales)}%`)
                  : "—"}
              </td>
              <td style={{ padding: "4px 8px", color: COLOR.muted, textAlign: "right" }}>
                {p.upper != null
                  ? (unidad === "mmdp" ? `${(p.upper / 1e9).toFixed(decimales)}` : `${fmt(p.upper, decimales)}%`)
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <InsightBox text={insight} />
    </div>
  );

  return (
    <div>
      <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace", lineHeight: 1.55,
                    background: COLOR.surface, border: `1px solid ${COLOR.border}`,
                    borderRadius: 6, padding: "10px 14px", marginBottom: 14 }}>
        <b style={{ color: COLOR.gold }}>Universo del pronóstico:</b> subconjunto de {fc.entidades_modelo?.length || 0} instituciones
        con historia ARIMA completa{fc.cobertura_pct != null ? ` (${fc.cobertura_pct}% del saldo del sistema)` : ""}.
        Las cifras observadas y proyectadas corresponden a ese subconjunto y <b>no</b> son comparables con el
        saldo total del reporte. El horizonte inicia en el corte posterior al último observado.
      </div>
      <ForecastChartCon
        saldoHist={hist} saldoProy={proy_s}
        imorHist={hist}  imorProy={proy_i}
      />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 12 }}>
        <PanelForecast
          titulo="Saldo Total — Proyección"
          proyecciones={proy_s}
          unidad="mmdp" decimales={2}
          insight={saldoData.insight}
        />
        <PanelForecast
          titulo="IMOR — Proyección"
          proyecciones={proy_i}
          unidad="%" decimales={4}
          insight={imorData.insight}
          advertencia={excluidas.length > 0 ? `Excluidas por modelo no confiable: ${excluidas.join(", ")}` : null}
        />
      </div>
      <div style={{ fontSize: 12, color: COLOR.dim, fontFamily: "monospace" }}>
        Modelos ARIMA_PLUS entrenados en BigQuery ML · {fc.entidades_modelo?.length || 0} instituciones
        {fc.cobertura_pct != null ? ` · Cobertura estimada: ${fc.cobertura_pct}% del sistema` : ""}
        {" · Horizonte: "}{fc.horizonte} cortes bimestrales · Intervalo de confianza 90%
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════════════
const ReporteConsumo = memo(function ReporteConsumo({ user }) {
  const [modo,          setModo]          = useState("sectorial");
  const [institucion,   setInstitucion]   = useState("");
  const [periodo,       setPeriodo]       = useState(PERIODOS[0]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [reporte,       setReporte]       = useState(null);
  const [exportando,    setExportando]    = useState(false);
  const [seccionActiva, setSeccionActiva] = useState("resumen");

  // ── Fetch reporte ───────────────────────────────────────────────────
  const generarReporte = async () => {
    if (modo === "institucional" && !institucion) {
      setError("Selecciona una institución.");
      return;
    }
    setLoading(true);
    setError(null);
    setReporte(null);
    try {
      const body = {
        modo,
        nombre_entidad: modo === "institucional" ? institucion : undefined,
        periodo: parseInt(periodo),
        usuario: user?.email || "",
      };
      const res = await fetch(`${API_URL}/reporte/consumo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      setReporte(data);
      setSeccionActiva("resumen");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Descarga HTML ───────────────────────────────────────────────────
  const exportarHTML = () => {
    if (!reporte) return;
    setExportando(true);
    try {
      const html = generarHTMLExport(reporte);
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      const per  = String(reporte.periodo);
      a.href     = url;
      a.download = `reporte_consumo_${per}_${(reporte.nombre_entidad || "sectorial").replace(/\s+/g, "_")}.html`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Error al exportar:", e);
    } finally {
      setExportando(false);
    }
  };

  const s = reporte?.secciones;

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden",
                  background: COLOR.bg }}>

      {/* TOP BAR */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        padding: "10px 20px", background: COLOR.surface,
        borderBottom: `1px solid ${COLOR.border}`, flexShrink: 0,
      }}>
        {/* Modo */}
        {["sectorial","institucional"].map(m => (
          <button key={m} onClick={() => setModo(m)} style={{
            padding: "5px 14px", borderRadius: 4, fontSize: 14,
            fontFamily: "monospace", cursor: "pointer",
            border: `1px solid ${modo === m ? COLOR.gold : COLOR.border}`,
            background: modo === m ? `${COLOR.gold}18` : "none",
            color: modo === m ? COLOR.gold : COLOR.muted,
          }}>
            {m === "sectorial" ? "Sectorial" : "Institucional"}
          </button>
        ))}

        {/* Institución */}
        {modo === "institucional" && (
          <select value={institucion} onChange={e => setInstitucion(e.target.value)} style={{
            background: COLOR.card, color: COLOR.text, border: `1px solid ${COLOR.border}`,
            borderRadius: 4, padding: "5px 10px", fontSize: 14, fontFamily: "monospace",
          }}>
            <option value="">— Seleccionar —</option>
            {INSTITUCIONES.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
        )}

        {/* Periodo */}
        <select value={periodo} onChange={e => setPeriodo(e.target.value)} style={{
          background: COLOR.card, color: COLOR.text, border: `1px solid ${COLOR.border}`,
          borderRadius: 4, padding: "5px 10px", fontSize: 14, fontFamily: "monospace",
        }}>
          {PERIODOS.map(p => (
            <option key={p} value={p}>{p.slice(0,4)}-{p.slice(4)}</option>
          ))}
        </select>

        {/* Generar */}
        <button onClick={generarReporte} disabled={loading} style={{
          padding: "5px 18px", borderRadius: 4, fontSize: 14, fontFamily: "monospace",
          cursor: loading ? "wait" : "pointer",
          border: `1px solid ${COLOR.gold}`,
          background: loading ? COLOR.surface : `${COLOR.gold}22`,
          color: loading ? COLOR.muted : COLOR.gold, fontWeight: 700,
        }}>
          {loading ? "Generando…" : "Generar reporte"}
        </button>

        {/* Exportar */}
        {reporte && (
          <button onClick={exportarHTML} disabled={exportando} style={{
            padding: "5px 14px", borderRadius: 4, fontSize: 14, fontFamily: "monospace",
            cursor: "pointer", border: `1px solid ${COLOR.border}`,
            background: "none", color: COLOR.muted,
          }}>
            {exportando ? "…" : "↓ HTML"}
          </button>
        )}
      </div>

      {/* ERROR */}
      {error && (
        <div style={{ margin: "10px 20px", padding: "10px 14px", borderRadius: 6,
                      background: `${COLOR.red}18`, border: `1px solid ${COLOR.red}44`,
                      fontSize: 15, color: COLOR.red, fontFamily: "monospace" }}>
          {error}
        </div>
      )}

      {/* LOADING */}
      {loading && (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                      flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 24, animation: "spin 1s linear infinite" }}>⟳</div>
          <div style={{ fontSize: 15, color: COLOR.muted, fontFamily: "monospace" }}>
            Consultando BigQuery y generando narrativas…
          </div>
          <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
        </div>
      )}

      {/* REPORTE */}
      {!loading && reporte && (
        <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>

          {/* META */}
          <div style={{ padding: "5px 18px", background: COLOR.surface,
                        borderBottom: `1px solid ${COLOR.border}`,
                        fontSize: 13, color: COLOR.muted, fontFamily: "monospace" }}>
            {reporte.nombre_entidad} · Periodo {reporte.periodo} · {reporte.tiempo_s}s
          </div>

          {/* TABS HORIZONTALES */}
          <div style={{ display: "flex", gap: 2, padding: "6px 12px",
                        borderBottom: `1px solid ${COLOR.border}`,
                        background: COLOR.surface, flexWrap: "wrap" }}>
            {SECCIONES.map(sec => {
              const active = seccionActiva === sec.id;
              const badge  = sec.id === "riesgo"
                ? (s?.anomalias?.filas?.length || 0)
                : 0;
              return (
                <button key={sec.id} onClick={() => setSeccionActiva(sec.id)} style={{
                  padding: "4px 12px", borderRadius: 4, fontSize: 13,
                  fontFamily: "monospace", cursor: "pointer",
                  border: `1px solid ${active ? COLOR.gold : COLOR.border}`,
                  background: active ? `${COLOR.gold}18` : "transparent",
                  color: active ? COLOR.gold : COLOR.muted,
                  fontWeight: active ? 700 : 400,
                }}>
                  {sec.icon} {sec.label}
                  {badge > 0 && (
                    <span style={{ marginLeft: 4, background: `${COLOR.red}22`, color: COLOR.red,
                                   padding: "0 4px", borderRadius: 8, fontSize: 12 }}>
                      {badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* CONTENIDO */}
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", background: COLOR.bg }}>
            {(() => {
              const sec = SECCIONES.find(x => x.id === seccionActiva);
              return sec ? <SectionHeader icon={sec.icon} title={sec.label} /> : null;
            })()}
            {seccionActiva === "resumen"     && <SeccionResumen    s={s} />}
            {seccionActiva === "calidad"     && <SeccionCalidad    s={s} />}
            {seccionActiva === "rollrates"   && <SeccionRollRates  s={s} />}
            {seccionActiva === "producto"    && <SeccionProducto   s={s} />}
            {seccionActiva === "territorio"  && <SeccionTerritorio s={s} />}
            {seccionActiva === "originacion" && <SeccionOriginacion s={s} />}
            {seccionActiva === "riesgo"      && <SeccionRiesgo      s={s} />}
            {seccionActiva === "pronostico"  && <SeccionPronostico  s={s} />}
          </div>
        </div>
      )}

      {/* ESTADO INICIAL */}
      {!loading && !reporte && !error && (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                      flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 32, color: COLOR.gold, opacity: 0.4 }}>◈</div>
          <div style={{ fontSize: 15, color: COLOR.dim, fontFamily: "monospace" }}>
            Configura los parámetros y presiona <strong style={{ color: COLOR.muted }}>Generar reporte</strong>
          </div>
        </div>
      )}
    </div>
  );
});

export default ReporteConsumo;
